import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { X, Play, FileText, Image as ImageIcon, CheckCircle, Clock, BookOpen, Award, ChevronRight, Users, GraduationCap, ClipboardCheck, Lock, Check, ListChecks } from 'lucide-react';
import axios from 'axios';
import FeedbackModal from './FeedbackModal';
import { useCelebration } from './CelebrationEffects';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sub-component: loads and displays all adaptive questions across all subchapters
const AdaptiveFullReview = ({ content, adaptiveCompletedSections, getFilteredSections, adaptiveSectionQuestions, adaptiveAnswers, adaptiveRevisit, selectedLanguage, getTranslatedQuestionText, getTranslatedOptionText, API: apiUrl }) => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadAllQuestions = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const filteredSections = getFilteredSections();
        const questionsGrouped = [];

        // If we have current session questions and answers (not revisit), use them directly
        if (!adaptiveRevisit && adaptiveSectionQuestions.length > 0) {
          const currentSection = filteredSections[0];
          questionsGrouped.push({
            sectionTitle: currentSection?.sectionTitle || 'Subchapter',
            questions: adaptiveSectionQuestions,
            answers: adaptiveAnswers
          });
          setAllQuestions(questionsGrouped);
          setLoading(false);
          return;
        }

        // Revisit mode: load questions for all adaptive-completed sections
        for (const section of filteredSections) {
          if (!adaptiveCompletedSections.has(section.globalIndex) && !adaptiveCompletedSections.has(section.legacyGlobalIndex)) continue;
          const sectionData = content?.sections?.[section.sectionIndex];
          if (!sectionData) continue;
          const sectionId = sectionData.id || sectionData.section_title;
          if (!content?.id || !sectionId) continue;

          try {
            const resp = await axios.get(
              `${apiUrl}/assessments/questions/section/${content.id}/${sectionId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (resp.data?.has_questions && resp.data.questions?.length > 0) {
              questionsGrouped.push({
                sectionTitle: section.sectionTitle,
                questions: resp.data.questions,
                answers: {} // On revisit, all answers were correct (user passed)
              });
            }
          } catch (e) { /* skip failed sections */ }
        }

        // Fallback: if adaptiveCompletedSections is empty, use current session questions
        if (questionsGrouped.length === 0 && adaptiveSectionQuestions.length > 0) {
          questionsGrouped.push({
            sectionTitle: filteredSections[0]?.sectionTitle || 'Subchapter',
            questions: adaptiveSectionQuestions,
            answers: adaptiveAnswers
          });
        }

        setAllQuestions(questionsGrouped);
      } catch (e) {
        console.error('Failed to load adaptive review questions:', e);
      }
      setLoading(false);
    };
    loadAllQuestions();
  }, []); // Run once on mount

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-sm text-gray-600">Loading answers...</span>
      </div>
    );
  }

  if (allQuestions.length === 0) {
    return <p className="text-center text-gray-500 py-8">No questions to review.</p>;
  }

  let questionCounter = 0;
  return (
    <div className="space-y-6">
      {allQuestions.map((group, gIdx) => (
        <div key={gIdx}>
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 mb-3">
            <span className="text-sm font-semibold text-purple-800">📘 {group.sectionTitle}</span>
          </div>
          <div className="space-y-4">
            {group.questions.map((q) => {
              questionCounter++;
              const userAnswer = group.answers[q.question_id];
              // On revisit with no stored answers, mark correct options as selected (user passed)
              const isRevisitNoAnswers = !userAnswer && adaptiveRevisit;
              return (
                <div key={q.question_id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">{questionCounter}</span>
                    <p className="font-medium text-gray-900 text-sm leading-relaxed">{getTranslatedQuestionText(q)}</p>
                  </div>
                  <div className="ml-10 space-y-2">
                    {q.options?.map(opt => {
                      const isCorrect = opt.is_correct;
                      const isSelected = isRevisitNoAnswers
                        ? isCorrect // On revisit, show correct answers as selected
                        : q.question_type === 'mcq_multiple'
                          ? (userAnswer || []).includes(opt.option_id)
                          : userAnswer === opt.option_id;
                      
                      let bgClass = 'bg-gray-50 border-gray-200';
                      let iconEl = null;
                      if (isCorrect) {
                        bgClass = 'bg-green-50 border-green-300';
                        iconEl = <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />;
                      } else if (isSelected && !isCorrect) {
                        bgClass = 'bg-red-50 border-red-300';
                        iconEl = <X className="w-4 h-4 text-red-500 flex-shrink-0" />;
                      }
                      
                      return (
                        <div key={opt.option_id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${bgClass}`}>
                          {iconEl || <div className="w-4 h-4 flex-shrink-0" />}
                          <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' : isSelected && !isCorrect ? 'text-red-800' : 'text-gray-700'}`}>
                            {getTranslatedOptionText(opt)}
                          </span>
                          {isSelected && isCorrect && (
                            <span className="text-xs text-green-600 italic flex-shrink-0">✓ Correct</span>
                          )}
                          {isSelected && !isCorrect && (
                            <span className="text-xs text-red-500 italic flex-shrink-0">Your answer</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(q.right_instruction || q.right_instruction_translations?.[selectedLanguage]) && (
                    <div className="ml-10 mt-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Explanation</p>
                      <p className="text-sm text-blue-800 leading-relaxed">{q.right_instruction_translations?.[selectedLanguage] || q.right_instruction}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const TrainingContentModal = ({ isOpen, onClose, contentId, trainingTopicId, selectedLanguage = 'English', isHybridAssessmentMode = false, hybridTopicName = '', institutionTrainingMode = null, institutionAssessmentMode = 'chapter_wise', institutionPassingPercentage = 80 }) => {
  console.log('[TrainingContentModal] Props:', { contentId, trainingTopicId, isHybridAssessmentMode });
  
  const { triggerSubchapterCelebration, triggerChapterCelebration, resetCelebrations, CelebrationOverlay } = useCelebration();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Helper to get the actual topic ID for feedback
  const getTopicIdForFeedback = () => {
    const topicId = trainingTopicId || contentId || content?.id;
    console.log('[TrainingContentModal] getTopicIdForFeedback:', topicId);
    return topicId;
  };
  
  // Helper function to check if user is Staff or Manager (they have identical MY ACTIVITY functionality)
  const isStaffOrManager = (userRole) => {
    return userRole === 'Staff' || userRole === 'Manager' || userRole === 'Reporting Manager';
  };
  
  // Helper function to get translated section title
  const getTranslatedSectionTitle = (section) => {
    // Check if section has title_translations
    if (section.section_title_translations && section.section_title_translations[selectedLanguage]) {
      return section.section_title_translations[selectedLanguage];
    }
    // Fallback to section_title (backward compatibility)
    return section.section_title;
  };

  const getTranslatedChapterTitle = () => {
    if (content?.title_translations?.[selectedLanguage]) {
      return content.title_translations[selectedLanguage];
    }
    return content?.title || content?.training_topic_name || '';
  };

  const getAssessmentTypeLabel = (phase) => {
    const labels = {
      pre: {
        English: 'Pre-Sub-Chapter Assessment',
        Hindi: 'प्री-सब-चैप्टर असेसमेंट'
      },
      post: {
        English: 'Post-Sub-Chapter Assessment',
        Hindi: 'पोस्ट-सब-चैप्टर असेसमेंट'
      }
    };
    return labels[phase]?.[selectedLanguage] || labels[phase]?.English || '';
  };

  const getScwAssessmentSupportText = (phase) => {
    if (phase === 'pre') {
      return selectedLanguage === 'Hindi'
        ? 'प्री-सब-चैप्टर असेसमेंट। आपका स्कोर केवल इनसाइट्स के लिए रिकॉर्ड किया जाता है; पासिंग की कोई आवश्यकता नहीं है।'
        : 'Pre-Sub-Chapter Assessment. Your score is recorded for insights only; there is no passing requirement.';
    }
    return selectedLanguage === 'Hindi'
      ? `पोस्ट-सब-चैप्टर असेसमेंट। इस सबचैप्टर को पूरा करने के लिए कम से कम ${resolvedPassingPct || 80}% स्कोर करें।`
      : `Post-Sub-Chapter Assessment. Score at least ${resolvedPassingPct || 80}% to complete this subchapter.`;
  };
  
  // Helper function to get translated question text
  const getTranslatedQuestionText = (question) => {
    if (question.question_text_translations && question.question_text_translations[selectedLanguage]) {
      return question.question_text_translations[selectedLanguage];
    }
    return question.question_text;
  };
  
  // Helper function to get translated option text
  const getTranslatedOptionText = (option) => {
    if (option.text_translations && option.text_translations[selectedLanguage]) {
      return option.text_translations[selectedLanguage];
    }
    return option.text;
  };
  
  const [activePreview, setActivePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [showMobileSubchapters, setShowMobileSubchapters] = useState(false);
  const videoRef = useRef(null);
  const pdfViewStartTime = useRef(null);
  const [viewedSections, setViewedSections] = useState(new Set());
  const [currentProgress, setCurrentProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('untapped');
  // Track the backend-loaded status to prevent stale state from causing incorrect 100% display
  const [backendLoadedStatus, setBackendLoadedStatus] = useState(null);
  const [topicAssessment, setTopicAssessment] = useState(null);
  const [assessmentFetchDone, setAssessmentFetchDone] = useState(false); // true when fetchTopicAssessment completes
  const [assessmentAttempts, setAssessmentAttempts] = useState([]);
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [assessmentSubmitted, setAssessmentSubmitted] = useState(false);
  const [assessmentScore, setAssessmentScore] = useState(null);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [totalQuestionsCount, setTotalQuestionsCount] = useState(0);
  const [isSubmittingAssessment, setIsSubmittingAssessment] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewAttempt, setReviewAttempt] = useState(null);
  const [topicFeedback, setTopicFeedback] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const hasAutoPlayedRef = useRef(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [hasPendingFeedback, setHasPendingFeedback] = useState(false);
  const [pendingFeedbackTopic, setPendingFeedbackTopic] = useState(null);
  const [isMandatoryFeedback, setIsMandatoryFeedback] = useState(false);
  const [shouldTriggerFeedbackOnClose, setShouldTriggerFeedbackOnClose] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showPreAssessmentNotes, setShowPreAssessmentNotes] = useState(false);
  const [preAssessmentNotesContent, setPreAssessmentNotesContent] = useState('');
  
  // Right/Wrong instruction display states
  const [currentInstructionMessage, setCurrentInstructionMessage] = useState(null); // { type: 'correct'|'incorrect', message: 'html' }
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set()); // Track which questions have been answered

  // === ADAPTIVE LEARNING SECTION STATES ===
  const [adaptiveMode, setAdaptiveMode] = useState(false); // true when section has adaptive questions showing
  const [adaptiveSectionQuestions, setAdaptiveSectionQuestions] = useState([]); // questions for current section
  const [adaptiveCurrentIndex, setAdaptiveCurrentIndex] = useState(0);
  const [adaptiveAnswers, setAdaptiveAnswers] = useState({});
  const [adaptiveSubmitted, setAdaptiveSubmitted] = useState(false);
  const [adaptiveScore, setAdaptiveScore] = useState(null);
  const [adaptiveCorrectCount, setAdaptiveCorrectCount] = useState(0);
  const [adaptiveAnsweredSet, setAdaptiveAnsweredSet] = useState(new Set());
  const [adaptiveInstructionMsg, setAdaptiveInstructionMsg] = useState(null);
  const [adaptiveWrongAnswer, setAdaptiveWrongAnswer] = useState(false); // true when wrong answer triggers redirect flow
  const [showSummaryView, setShowSummaryView] = useState(false); // show summary video/PDF after adaptive pass
  const [currentSectionMeta, setCurrentSectionMeta] = useState(null); // section metadata (with summary_video/pdf)
  const [showAdaptiveReview, setShowAdaptiveReview] = useState(false); // show answer review on completion screen
  const [adaptiveRevisit, setAdaptiveRevisit] = useState(false); // true when revisiting a completed section
  const [showKeyHighlightsCompletion, setShowKeyHighlightsCompletion] = useState(false); // show completion screen after Finish on last section
  const [adaptiveSuccessPath, setAdaptiveSuccessPath] = useState(false); // true when user passed adaptive questions (no regular assessment needed)
  const [adaptiveCompletedSections, setAdaptiveCompletedSections] = useState(new Set()); // global indexes of sections completed via adaptive success
  const [viewingFullModuleFromAdaptive, setViewingFullModuleFromAdaptive] = useState(false); // user opted into full module after adaptive pass — show "Back to Summary"
  const [showAdaptiveInstructions, setShowAdaptiveInstructions] = useState(false); // show instruction screen before adaptive questions
  const [showAdaptiveFallback, setShowAdaptiveFallback] = useState(false); // show fallback popup when topic not ready for adaptive
  const [adaptiveFailedSession, setAdaptiveFailedSession] = useState(false); // true after user fails adaptive — disables adaptive for remaining sections and requires chapter assessment
  const [isTopicAdaptiveReady, setIsTopicAdaptiveReady] = useState(null); // null=loading, true/false from backend
  const [showCompletionScreen, setShowCompletionScreen] = useState(false); // show completed result screen on revisit
  const [showCompletionReview, setShowCompletionReview] = useState(false); // show inline answer review within completion screen
  const [completionScreenDismissed, setCompletionScreenDismissed] = useState(false); // user dismissed completion screen, allow free navigation
  const pendingFallbackSectionRef = useRef(null); // stores section info for deferred handleView after fallback acknowledgment

  // === INSTITUTION LEARNING MODE (Adaptive vs Conventional) ===
  // The chapter intro + flow must respect the institution's configured learning mode, not just
  // whether the topic has adaptive questions. Provided by the caller; fetched as a fallback.
  const [resolvedInstitutionMode, setResolvedInstitutionMode] = useState(institutionTrainingMode); // 'adaptive' | 'conventional' | null
  const [showConventionalIntro, setShowConventionalIntro] = useState(false); // conventional "Before You Begin" popup
  const conventionalIntroShownRef = useRef(null); // contentId the intro has already been shown for

  // === SUB-CHAPTER-WISE ASSESSMENT (Conventional) ===
  // Flow per sub-chapter: PRE (score only) -> content -> POST (must pass institution %) -> unlock next.
  const [resolvedAssessmentMode, setResolvedAssessmentMode] = useState(institutionAssessmentMode); // 'chapter_wise' | 'subchapter_wise'
  const [resolvedPassingPct, setResolvedPassingPct] = useState(institutionPassingPercentage);
  const [scwPhase, setScwPhase] = useState(null); // null | 'pre' | 'post'
  const [scwQuestions, setScwQuestions] = useState([]);
  const [scwIndex, setScwIndex] = useState(0);
  const [scwAnswers, setScwAnswers] = useState({});
  const [scwResult, setScwResult] = useState(null); // { score, passed } shown after a POST submit
  const [scwSubmitting, setScwSubmitting] = useState(false);
  const [scwSubmittedIds, setScwSubmittedIds] = useState({}); // per-question: Submit clicked
  const [scwShowScore, setScwShowScore] = useState(false);    // PRE end-of-assessment score screen
  const [videoEnded, setVideoEnded] = useState(false);        // true only after the current video fully ends
  const scwPhaseRef = useRef(null);            // synchronous mirror of scwPhase for navigation guards
  const scwSectionRef = useRef(null);          // { section, index, sectionId, key }
  const scwPreDoneRef = useRef(new Set());     // section keys whose PRE has been completed
  const scwContentDoneRef = useRef(new Set()); // section keys whose CONTENT has been completed (post pending)
  const scwPassedRef = useRef(new Set());      // section keys whose POST has been passed
  const scwQuestionsCacheRef = useRef({});     // section key -> fetched questions

  // Persist per-sub-chapter progress (pre/content) to localStorage so a refresh or re-login
  // resumes from the last completed step instead of restarting the pre-assessment.
  const getScwUid = () => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.id || 'anon'; } catch { return 'anon'; }
  };
  const scwProgKey = () => `scw_prog_${getScwUid()}_${contentId}`;
  const scwIntroKey = () => `scw_intro_${getScwUid()}_${contentId}`;
  const loadScwProgress = () => {
    try { return JSON.parse(localStorage.getItem(scwProgKey()) || '{}'); } catch { return {}; }
  };
  const saveScwProgress = () => {
    const obj = {};
    scwPreDoneRef.current.forEach((k) => { obj[k] = { ...(obj[k] || {}), pre: 1 }; });
    scwContentDoneRef.current.forEach((k) => { obj[k] = { ...(obj[k] || {}), content: 1 }; });
    try { localStorage.setItem(scwProgKey(), JSON.stringify(obj)); } catch {}
  };

  const getResolvedUserRole = () => {
    if (user?.role) return user.role;
    try {
      return JSON.parse(localStorage.getItem('user') || '{}')?.role || null;
    } catch {
      return null;
    }
  };

  // Adaptive-ready chapters must always start in adaptive flow for staff/manager roles.
  // Every subchapter still tries adaptive questions first, even after a previous failure.
  // The chapter assessment requirement is added separately (see adaptiveFailedSession usages).
  const getEffectiveTrainingMode = () => {
    // Respect the institution's configured learning mode first — if the institution selected
    // Conventional Learning, never run the adaptive flow (or show adaptive instructions),
    // regardless of whether the topic happens to have adaptive questions.
    if (resolvedInstitutionMode === 'conventional') return 'conventional';
    if (isStaffOrManager(getResolvedUserRole()) && isTopicAdaptiveReady === true) return 'adaptive';
    return 'conventional';
  };

  // Keep the resolved institution mode in sync with the prop; fetch as a fallback if not provided.
  useEffect(() => {
    if (institutionTrainingMode) {
      setResolvedInstitutionMode(institutionTrainingMode);
      return;
    }
    if (isStaffOrManager(getResolvedUserRole())) {
      (async () => {
        try {
          const token = localStorage.getItem('token');
          const r = await axios.get(`${API}/staff/training-mode`, { headers: { Authorization: `Bearer ${token}` } });
          setResolvedInstitutionMode(r.data?.training_mode || 'adaptive');
          setResolvedAssessmentMode(r.data?.assessment_mode || 'chapter_wise');
          setResolvedPassingPct(r.data?.passing_percentage || 80);
        } catch {
          setResolvedInstitutionMode('adaptive');
        }
      })();
    }
  }, [institutionTrainingMode]);

  // Keep assessment mode / passing % in sync with props when provided by the caller.
  useEffect(() => {
    if (institutionAssessmentMode) setResolvedAssessmentMode(institutionAssessmentMode);
    if (institutionPassingPercentage) setResolvedPassingPct(institutionPassingPercentage);
  }, [institutionAssessmentMode, institutionPassingPercentage]);

  // Reset sub-chapter-wise runtime when the modal closes or the chapter changes.
  useEffect(() => {
    setScwPhase(null); scwPhaseRef.current = null;
    setScwQuestions([]); setScwIndex(0); setScwAnswers({}); setScwResult(null);
    setScwSubmittedIds({}); setScwShowScore(false);
    scwSectionRef.current = null;
    scwPassedRef.current = new Set();
    scwQuestionsCacheRef.current = {};
    // Hydrate persisted per-sub-chapter progress so we resume from the last completed step
    const prog = (isOpen && contentId) ? loadScwProgress() : {};
    const pre = new Set(); const contentDone = new Set();
    Object.entries(prog).forEach(([k, v]) => { if (v?.pre) pre.add(k); if (v?.content) contentDone.add(k); });
    scwPreDoneRef.current = pre;
    scwContentDoneRef.current = contentDone;
  }, [isOpen, contentId]);

  // True when this staff member's institution uses Conventional + Sub-chapter-wise assessment.
  const isSubchapterWise = () =>
    isStaffOrManager(getResolvedUserRole())
    && resolvedInstitutionMode === 'conventional'
    && resolvedAssessmentMode === 'subchapter_wise';

  // Show the Conventional "Before You Begin" intro once per chapter open (conventional institutions only).
  useEffect(() => {
    if (!isOpen) {
      setShowConventionalIntro(false);
      conventionalIntroShownRef.current = null;
      return;
    }
    if (content?.id && isStaffOrManager(getResolvedUserRole())
        && resolvedInstitutionMode === 'conventional'
        && conventionalIntroShownRef.current !== content.id) {
      conventionalIntroShownRef.current = content.id;
      // Show "Before You Begin" only ONCE per chapter (persisted) — not on every open/refresh/sub-chapter
      let seen = false;
      try { seen = localStorage.getItem(scwIntroKey()) === '1'; } catch {}
      if (!seen) setShowConventionalIntro(true);
    }
  }, [isOpen, content, resolvedInstitutionMode]);

  // A REAL chapter-assessment pass — excludes the 'adaptive_complete' shortcut record.
  // Used when a fallback occurred, since the shortcut record must not count as passing.
  const hasRealChapterAssessmentPass = () => {
    const passScore = topicAssessment?.pass_score_percentage || 80;
    return assessmentAttempts.some(a =>
      a.assessment_type !== 'adaptive_complete' &&
      (a.passed || (a.score_percentage || 0) >= passScore)
    );
  };

  // True when a fallback to full content occurred for any subchapter and the mandatory
  // chapter assessment has not yet been passed. This is the single source of truth for
  // "assessment pending" — the chapter must NOT be shown as completed while this is true.
  const isChapterAssessmentPending = () => {
    if (!topicAssessment || !adaptiveFailedSession) return false;
    return !hasRealChapterAssessmentPass();
  };

  // CRITICAL FIX: Load user data immediately on component mount
  // This ensures user role is available for ALL functions that need it
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        console.log('[TrainingContentModal] User loaded on mount:', parsedUser.role, parsedUser.full_name || parsedUser.name);
      } catch (error) {
        console.error('[TrainingContentModal] Failed to parse user data:', error);
      }
    }
  }, []); // Run once on mount

  // Check for pending feedback before opening modal
  useEffect(() => {
    if (isOpen && contentId) {
      checkPendingFeedback();
    }
  }, [isOpen, contentId]);

  useEffect(() => {
    if (isOpen && contentId && !hasPendingFeedback) {
      // Reset state when opening a new topic
      console.log('[TrainingContentModal] Opening topic:', contentId, 'isHybridAssessmentMode:', isHybridAssessmentMode);
      setViewedSections(new Set()); // Clear viewed sections first
      setContent(null); // Clear previous content
      setActivePreview(null); // Clear preview
      setCurrentSectionIndex(0); // Reset to first section
      setAssessmentAttempts([]); // Clear assessment attempts
      setLoading(true); // Show loading state
      
      // CRITICAL FIX: Reset progress/status to prevent stale data from previous topic
      // causing incorrect 100% completion display on reopen
      setCurrentProgress(0);
      setCurrentStatus('untapped');
      setBackendLoadedStatus(null); // Clear backend status so we fetch fresh data
      
      // CRITICAL FIX: Reset ALL assessment-related states to prevent context mixing
      setShowAssessment(false);
      setAssessmentQuestions([]);
      setCurrentAnswers({});
      setAssessmentSubmitted(false);
      setAssessmentScore(null);
      setCorrectAnswersCount(0);
      setTotalQuestionsCount(0);
      setIsReviewMode(false);
      setReviewAttempt(null);
      setTopicAssessment(null);
      setAssessmentFetchDone(false);
      setCurrentQuestionIndex(0);
      setCurrentInstructionMessage(null);
      setAnsweredQuestions(new Set());
      // Reset adaptive section states
      setAdaptiveMode(false);
      setAdaptiveSectionQuestions([]);
      setAdaptiveCurrentIndex(0);
      setAdaptiveAnswers({});
      setAdaptiveSubmitted(false);
      setAdaptiveScore(null);
      setAdaptiveCorrectCount(0);
      setAdaptiveAnsweredSet(new Set());
      setAdaptiveInstructionMsg(null);
      setAdaptiveWrongAnswer(false);
      setShowSummaryView(false);
      setCurrentSectionMeta(null);
      setShowAdaptiveReview(false);
      setAdaptiveRevisit(false);
      setShowKeyHighlightsCompletion(false);
      setAdaptiveSuccessPath(false);
      setAdaptiveCompletedSections(new Set());
      setViewingFullModuleFromAdaptive(false);
      setShowAdaptiveInstructions(false);
      setShowAdaptiveFallback(false);
      setShowCompletionScreen(false);
      setShowCompletionReview(false);
      setCompletionScreenDismissed(false);
      pendingFallbackSectionRef.current = null;
      resetCelebrations();
      // For hybrid topics, start with assessment tab; otherwise content tab
      setActiveTab(isHybridAssessmentMode ? 'assessment' : 'content');
      
      // CRITICAL FIX: Load user data synchronously BEFORE calling any fetch functions
      // This ensures user role is available for authorization checks
      const userData = localStorage.getItem('user');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        console.log('[TrainingContentModal] User loaded:', parsedUser.role, parsedUser.full_name || parsedUser.name);
      }
      
      // Load data in proper sequence to avoid race conditions
      const initializeTopicData = async () => {
        await loadViewedSections(); // MUST complete before progress calculation
        // Load persisted adaptive-completed sections so revisits land on Great Job screen
        try {
          const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
          const stored = localStorage.getItem(`adaptive_completed_${userId}_${contentId}`);
          if (stored) setAdaptiveCompletedSections(new Set(JSON.parse(stored)));
          const failedFlag = localStorage.getItem(`adaptive_failed_${userId}_${contentId}`);
          if (failedFlag === 'true') {
            setAdaptiveFailedSession(true);
            // Self-heal: ensure the backend has the fallback flag persisted so the dashboard
            // listing matches the popup (fixes stale 100% records from before the flag existed).
            try {
              const token = localStorage.getItem('token');
              await axios.post(`${API}/staff/adaptive-fallback`, {
                training_topic_id: contentId
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
            } catch (e) { /* non-fatal */ }
          }
        } catch (e) { /* ignore */ }
        fetchContent();
        fetchTopicAssessment();
        fetchTopicFeedback();
        fetchAdaptiveReadyStatus();
      };
      
      initializeTopicData();
    }
  }, [isOpen, contentId, hasPendingFeedback]);

  useEffect(() => {
    // CRITICAL FIX: Add safeguards to prevent calculation with stale/incomplete data
    if (content && isStaffOrManager(user?.role) && contentId && viewedSections !== null) {
      const filteredSections = getFilteredSections();
      const totalFilteredSections = filteredSections.length;
      
      // Count how many filtered sections are viewed
      const viewedFilteredCount = filteredSections.filter(s => isSectionViewed(s)).length;
      const allSectionsViewed = viewedFilteredCount === totalFilteredSections && totalFilteredSections > 0;
      
      // Check if assessment exists and if passed
      const hasAssessment = !!topicAssessment;
      const assessmentPassed = assessmentAttempts.some(attempt => attempt.passed || attempt.score_percentage >= 80);
      const assessmentStateReady = assessmentFetchDone || hasAssessment;
      const hasAssessmentForProgress = hasAssessment || (!assessmentStateReady && allSectionsViewed);
      
      // CRITICAL FIX: Use backend-loaded status for "already completed" check
      // This prevents stale React state from incorrectly showing 100%
      // For ONLINE topics with assessment: 100% requires assessment passed
      // The isAlreadyCompleted shortcut should ONLY apply if backend says completed AND assessment is actually passed
      const backendSaysCompleted = backendLoadedStatus === 'completed';
      
      let progress, status;
      
      // CRITICAL FIX for Online topics:
      // Only use the "already completed" shortcut if:
      // 1. Backend status is 'completed' AND
      // 2. Assessment lookup has finished AND either there's no assessment, OR the assessment is actually passed
      // This prevents the bug where reopening popup shows 100% when assessment is pending
      const canUseCompletedShortcut = backendSaysCompleted && assessmentStateReady && (!hasAssessment || assessmentPassed);
      
      if (canUseCompletedShortcut) {
        // Topic is truly completed - maintain 100% status
        progress = 100;
        status = 'completed';
        console.log('[Progress Calc] Topic truly completed (backend + assessment check) - maintaining 100% status');
      } else {
        // Calculate progress considering both sections and assessment
        // Use filtered counts to only consider sections in selected language.
        // After adaptive failure, chapter assessment is required — treat as conventional
        // for progress purposes so the 80/20 split applies.
        const isAdaptive = getEffectiveTrainingMode() === 'adaptive' && !adaptiveFailedSession;
        progress = calculateProgress(
          { size: viewedFilteredCount }, // Use filtered viewed count
          totalFilteredSections,          // Use filtered total count
          hasAssessmentForProgress,
          assessmentPassed,
          isAdaptive
        );

        status = determineStatus(progress, hasAssessmentForProgress, assessmentPassed, isAdaptive);
      }
      
      console.log('[Progress Calc] Topic:', contentId, 'Progress:', progress, 'Status:', status, 
                  'BackendStatus:', backendLoadedStatus, 'HasAssessment:', hasAssessment, 'AssessmentPassed:', assessmentPassed);
      
      setCurrentProgress(progress);
      setCurrentStatus(status);
    }
  }, [content, viewedSections, user, topicAssessment, assessmentAttempts, assessmentFetchDone, selectedLanguage, topicFeedback, contentId, backendLoadedStatus, adaptiveFailedSession]);

  // v1.1.77: Force video reload and auto-play when activePreview changes OR language changes
  // This ensures video auto-plays after language switch even when fallback to same content
  useEffect(() => {
    // New content is loading — the fallback CTA must not show until THIS video ends
    setVideoEnded(false);
    if (videoRef.current && activePreview?.content_type === 'video') {
      const videoUrl = activePreview.file_url || activePreview.file_name;
      console.log('[Video] Active preview or language changed, loading video:', videoUrl);
      videoRef.current.load(); // Force reload the video source
      
      // Attempt autoplay after a brief delay to allow video element to initialize
      setTimeout(() => {
        if (videoRef.current) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.log('Auto-play prevented by browser:', error);
            });
          }
        }
      }, 100);
    }
  }, [activePreview?.file_url, activePreview?.file_name, selectedLanguage]);

  // Fetch assessment attempts whenever topicAssessment is loaded
  useEffect(() => {
    if (topicAssessment?.questionnaire_id && isStaffOrManager(user?.role)) {
      console.log('[Assessment] topicAssessment loaded, fetching attempts');
      fetchAssessmentAttempts(topicAssessment.questionnaire_id);
    }
  }, [topicAssessment?.questionnaire_id, user?.role]);

  const checkPendingFeedback = async () => {
    // This function is now simplified - we check feedback status dynamically on modal close
    // No need to check or block on modal open
    console.log('[TrainingContentModal] Opening topic - feedback will be checked on close if completed');
  };

  // Auto-play first section on modal open — navigate to next incomplete section
  useEffect(() => {
    if (content && !hasAutoPlayedRef.current && autoPlayEnabled) {
      
      // CRITICAL: Wait for adaptive-ready check to complete before auto-playing.
      // This prevents a transient conventional launch before we know adaptive readiness.
      if (isStaffOrManager(getResolvedUserRole()) && isTopicAdaptiveReady === null) {
        return; // Will re-fire when isTopicAdaptiveReady resolves
      }

      // Check if topic is already completed — show per-section completion on revisit
      // BUG FIX: Only show completion when there's actual viewed-section progress.
      // If a Hospital Admin reset the topic, viewedSections will be empty even if backend
      // status is briefly stale, so we avoid the false "Chapter Completed" screen.
      if (isStaffOrManager(user?.role) && backendLoadedStatus === 'completed' && viewedSections.size > 0) {
        if (topicAssessment && !isSubchapterWise()) {
          // Topic has assessment — wait for attempts to load before deciding.
          // If a fallback occurred, the adaptive_complete shortcut must NOT count as passing.
          const assessmentPassed = adaptiveFailedSession
            ? hasRealChapterAssessmentPass()
            : assessmentAttempts.some(attempt =>
                attempt.passed || attempt.score_percentage >= (topicAssessment?.pass_score_percentage || 80)
              );
          if (assessmentPassed) {
            hasAutoPlayedRef.current = true;
            console.log('[TrainingContentModal] Topic completed with assessment passed — showing section completion');
            // Navigate to first section and show per-section completion view
            const filteredSections = getFilteredSections();
            if (filteredSections.length > 0) {
              const section = filteredSections[0];
              handleView(section.langVersion, section.globalIndex, section.sectionTitle);
              setCurrentSectionIndex(0);
              
              // Check if this section was completed via adaptive learning
              if (isSectionAdaptivelyCompleted(section)) {
                // Route to adaptive completion screen instead of conventional
                if (content?.sections?.[section.sectionIndex]) {
                  setCurrentSectionMeta(content.sections[section.sectionIndex]);
                }
                setAdaptiveMode(true);
                setAdaptiveRevisit(true);
                setAdaptiveSubmitted(true);
                setAdaptiveScore(prev => prev ?? 100);
                setAdaptiveSuccessPath(true);
                // Lazy-load adaptive questions for review
                const sectionData = content?.sections?.[section.sectionIndex];
                const sectionId = sectionData?.id || sectionData?.section_title;
                if (content?.id && sectionId) {
                  const token = localStorage.getItem('token');
                  axios.get(
                    `${API}/assessments/questions/section/${content.id}/${sectionId}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  ).then(resp => {
                    if (resp.data?.has_questions && resp.data.questions?.length > 0) {
                      setAdaptiveSectionQuestions(resp.data.questions);
                      setAdaptiveCorrectCount(resp.data.questions.length);
                    }
                  }).catch(() => {});
                }
                return;
              }
            }
            setShowCompletionScreen(true);
            return;
          }
          // Assessment exists but not passed yet — don't show completion, proceed normally
        } else {
          // No assessment configured — topic is completed, show per-section completion
          hasAutoPlayedRef.current = true;
          console.log('[TrainingContentModal] Topic completed (no assessment) — showing section completion');
          const filteredSections = getFilteredSections();
          if (filteredSections.length > 0) {
            const section = filteredSections[0];
            handleView(section.langVersion, section.globalIndex, section.sectionTitle);
            setCurrentSectionIndex(0);
            
            // Check if this section was completed via adaptive learning
            if (isSectionAdaptivelyCompleted(section)) {
              if (content?.sections?.[section.sectionIndex]) {
                setCurrentSectionMeta(content.sections[section.sectionIndex]);
              }
              setAdaptiveMode(true);
              setAdaptiveRevisit(true);
              setAdaptiveSubmitted(true);
              setAdaptiveScore(prev => prev ?? 100);
              setAdaptiveSuccessPath(true);
              const sectionData = content?.sections?.[section.sectionIndex];
              const sectionId = sectionData?.id || sectionData?.section_title;
              if (content?.id && sectionId) {
                const token = localStorage.getItem('token');
                axios.get(
                  `${API}/assessments/questions/section/${content.id}/${sectionId}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                ).then(resp => {
                  if (resp.data?.has_questions && resp.data.questions?.length > 0) {
                    setAdaptiveSectionQuestions(resp.data.questions);
                    setAdaptiveCorrectCount(resp.data.questions.length);
                  }
                }).catch(() => {});
              }
              return;
            }
          }
          setShowCompletionScreen(true);
          return;
        }
      }
      
      hasAutoPlayedRef.current = true;

      // Get filtered sections based on selected language
      const filteredSections = getFilteredSections();

      if (filteredSections.length > 0) {
        // For staff/manager: find the first section that hasn't been completed yet
        let targetIndex = 0;
        if (isStaffOrManager(user?.role)) {
          const firstIncomplete = filteredSections.findIndex(s => !isSectionViewed(s));
          if (firstIncomplete >= 0) {
            targetIndex = firstIncomplete;
          } else if (topicAssessment && adaptiveFailedSession) {
            // All sections viewed but assessment pending (adaptive fallback) — go to assessment.
            // Exclude the adaptive_complete shortcut when deciding "passed".
            if (!hasRealChapterAssessmentPass()) {
              console.log('[AutoPlay] All sections viewed, assessment pending — launching assessment');
              const section = filteredSections[0];
              handleView(section.langVersion, section.globalIndex, section.sectionTitle);
              setCurrentSectionIndex(0);
              fetchPreAssessmentNotes();
              setShowPreAssessmentNotes(true);
              return;
            }
            targetIndex = 0;
          } else {
            targetIndex = 0;
          }
        }
        const targetSection = filteredSections[targetIndex];
        // Route through handleSectionClick so adaptive questions are checked
        handleSectionClick(targetSection, targetIndex, filteredSections);
      }
    }
  }, [content, selectedLanguage, backendLoadedStatus, assessmentAttempts, topicAssessment, isTopicAdaptiveReady, adaptiveFailedSession]);

  // CRITICAL FIX v1.1.77: Update activePreview AND trigger auto-play when language changes
  // This ensures the currently playing video updates to the new language's content
  // and auto-plays the video just like on initial load
  useEffect(() => {
    // Only update if we already have an active preview (user is viewing content)
    // and content is loaded
    if (activePreview && content && hasAutoPlayedRef.current) {
      const filteredSections = getFilteredSections();
      
      // Find the section at the current index
      if (filteredSections.length > 0 && currentSectionIndex < filteredSections.length) {
        const currentSection = filteredSections[currentSectionIndex];
        
        // Get URLs for comparison
        const newFileUrl = currentSection.langVersion.file_url || currentSection.langVersion.file_name;
        const currentFileUrl = activePreview.file_url || activePreview.file_name;
        
        // v1.1.77: Always update activePreview on language change to trigger auto-play
        // Even if the URL is the same (fallback scenario), we need to trigger the auto-play effect
        console.log('[TrainingContentModal] Language changed to:', selectedLanguage);
        console.log('[TrainingContentModal] Current section:', currentSectionIndex);
        
        if (newFileUrl !== currentFileUrl) {
          console.log('[TrainingContentModal] URL changed, updating activePreview');
          console.log('[TrainingContentModal] Old URL:', currentFileUrl);
          console.log('[TrainingContentModal] New URL:', newFileUrl);
        } else {
          console.log('[TrainingContentModal] Same URL (fallback), triggering reload for auto-play');
        }
        
        // Always call handleView to ensure activePreview is updated
        // This triggers the auto-play useEffect
        handleView(currentSection.langVersion, currentSection.globalIndex, currentSection.sectionTitle);
      }
    }
  }, [selectedLanguage]);

  // Reset auto-play flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasAutoPlayedRef.current = false;
      setCurrentSectionIndex(0);
    }
  }, [isOpen]);

  const loadViewedSections = async () => {
    // Make localStorage key user-specific to prevent cross-user data contamination
    const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
    const storageKey = `viewed_sections_${userId}_${contentId}`;
    
    try {
      // First, try to load from backend to get the most recent data
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/staff/progress/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        // CRITICAL FIX: Load the backend status and completion percentage
        // This ensures we use the actual stored state, not recalculated values
        const backendStatus = response.data.status || 'yet_to_start';
        const backendPercentage = response.data.completion_percentage || 0;
        console.log('[TrainingContentModal] Backend progress loaded - status:', backendStatus, 'percentage:', backendPercentage);
        setBackendLoadedStatus(backendStatus);

        // Server-side fallback flag is the source of truth (works across browsers/devices)
        if (response.data.adaptive_fallback === true) {
          setAdaptiveFailedSession(true);
        }
        
        if (response.data.viewed_sections && response.data.viewed_sections.length > 0) {
          console.log('[TrainingContentModal] Loaded viewed sections from backend:', response.data.viewed_sections.length);
          const sections = new Set(response.data.viewed_sections);
          setViewedSections(sections);
          // Also save to localStorage for offline access
          saveViewedSections(sections);
        } else {
          // Backend reports no viewed sections (e.g. fresh topic or admin reset).
          // Clear local caches so completion screens don't fire from stale data.
          setViewedSections(new Set());
          try {
            localStorage.removeItem(storageKey);
            localStorage.removeItem(`adaptive_completed_${userId}_${contentId}`);
          } catch (e) { /* ignore */ }
          setAdaptiveCompletedSections(new Set());
        }
        return;
      }
    } catch (error) {
      console.log('[TrainingContentModal] Could not fetch from backend, using localStorage fallback:', error.message);
    }
    
    // Fallback to localStorage if backend fetch fails
    const stored = localStorage.getItem(storageKey);
    console.log('[TrainingContentModal] Loading viewed sections for topic:', contentId, 'user:', userId);
    if (stored) {
      const sections = new Set(JSON.parse(stored));
      console.log('[TrainingContentModal] Found stored sections in localStorage:', sections.size);
      setViewedSections(sections);
    } else {
      console.log('[TrainingContentModal] No stored sections for this user, starting fresh');
      setViewedSections(new Set()); // Explicitly set empty set if no stored data
    }
  };

  const saveViewedSections = (sections) => {
    // Make localStorage key user-specific to prevent cross-user data contamination
    const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
    const storageKey = `viewed_sections_${userId}_${contentId}`;
    localStorage.setItem(storageKey, JSON.stringify([...sections]));
    console.log('[TrainingContentModal] Saved viewed sections for user:', userId, 'sections:', sections.size);
  };

  // Persist which sections were completed via adaptive success so revisits land on Great Job screen
  const getAdaptiveCompletedKey = () => {
    const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
    return `adaptive_completed_${userId}_${contentId}`;
  };
  const saveAdaptiveCompletedSections = (sectionsSet) => {
    try {
      localStorage.setItem(getAdaptiveCompletedKey(), JSON.stringify([...sectionsSet]));
    } catch (e) { /* ignore */ }
  };
  const markSectionAdaptivelyCompleted = (globalIndex) => {
    setAdaptiveCompletedSections(prev => {
      if (prev.has(globalIndex)) return prev;
      const next = new Set(prev);
      next.add(globalIndex);
      saveAdaptiveCompletedSections(next);
      return next;
    });
  };

  const calculateProgress = (viewedSectionsSet, totalSections, hasAssessment = false, assessmentPassed = false, isAdaptiveMode = false) => {
    if (totalSections === 0) return 0;
    
    // Calculate section progress
    const sectionProgress = (viewedSectionsSet.size / totalSections);
    
    if (hasAssessment && !isAdaptiveMode) {
      // Conventional learning with assessment: 80% for sections, 20% for assessment
      const sectionsWeight = 0.8;
      const assessmentWeight = 0.2;
      
      const sectionsScore = sectionProgress * sectionsWeight * 100;
      const assessmentScore = assessmentPassed ? (assessmentWeight * 100) : 0;
      
      return Math.round(sectionsScore + assessmentScore);
    } else {
      // Adaptive learning or no assessment: 100% based on sections only
      return Math.round(sectionProgress * 100);
    }
  };

  const determineStatus = (progress, hasAssessment = false, assessmentPassed = false, isAdaptiveMode = false) => {
    if (progress === 0) return 'untapped';
    
    // If conventional with assessment, require both full sections AND passed assessment for completion
    if (hasAssessment && !isAdaptiveMode) {
      if (progress === 100 && assessmentPassed) return 'completed';
      if (progress > 0) return 'in_progress';
    } else {
      // Adaptive or no assessment - just check progress
      if (progress === 100) return 'completed';
      if (progress > 0) return 'in_progress';
    }
    
    return 'untapped';
  };

  // Helper function to check if language version exists (with fallback)
  const hasLangVersion = (langVersions) => {
    if (!langVersions || langVersions.length === 0) return false;
    // Any language version counts - we'll use fallback if needed
    return true;
  };

  const getTotalSections = () => {
    if (!content) return 0;
    if (content.content_structure === 'standalone') {
      // For standalone, only 1 section if any language version exists
      return hasLangVersion(content.language_versions) ? 1 : 0;
    } else {
      // For sectioned content, count sections that have any language version
      let total = 0;
      content.sections?.forEach(section => {
        if (hasLangVersion(section.language_versions)) {
          total += 1; // Count only 1 per section
        }
      });
      return total;
    }
  };

  // Helper function to find matching language version with fallback logic
  const findMatchingLangVersion = (langVersions) => {
    if (!langVersions || langVersions.length === 0) return null;
    
    // Helper to check if a language version has valid file content
    const hasValidFile = (lv) => {
      return lv && (lv.file_url || lv.file_name);
    };
    
    // 1. First try exact match with valid file
    let match = langVersions.find(lv => lv.language === selectedLanguage && hasValidFile(lv));
    if (match) return match;
    
    // 2. Try case-insensitive match with valid file
    match = langVersions.find(lv => 
      lv.language?.toLowerCase() === selectedLanguage?.toLowerCase() && hasValidFile(lv)
    );
    if (match) return match;
    
    // 3. Fallback to first available language version WITH a valid file
    // This ensures content is always shown even if selected language doesn't have content
    const fallbackWithFile = langVersions.find(lv => hasValidFile(lv));
    if (fallbackWithFile) {
      console.log('[getFilteredSections] No valid file for', selectedLanguage, '- using fallback:', fallbackWithFile.language);
      return fallbackWithFile;
    }
    
    // 4. Last resort - return first version even if no file (let UI handle the error)
    console.log('[getFilteredSections] No valid files found, returning first version:', langVersions[0]?.language);
    return langVersions[0];
  };

  const getFilteredSections = () => {
    if (!content) return [];
    
    const sections = [];
    
    if (content.content_structure === 'standalone') {
      // For standalone content, find the version matching selected language
      const matchingVersion = findMatchingLangVersion(content.language_versions);
      if (matchingVersion) {
        sections.push({
          sectionTitle: getTranslatedChapterTitle(),
          langVersion: matchingVersion,
          globalIndex: 'standalone:0',
          legacyGlobalIndex: 0,
          sectionIndex: 0,
          isStandalone: true
        });
      }
    } else {
      // For sectioned content, get each section's matching language version
      content.sections?.forEach((section, sectionIndex) => {
        const matchingVersion = findMatchingLangVersion(section.language_versions);
        if (matchingVersion) {
          // Legacy index used language-version offsets; keep it only for reading old saved progress.
          const legacyBaseIndex = content.sections.slice(0, sectionIndex).reduce(
            (acc, s) => acc + (s.language_versions?.length || 0), 0
          );
          const langIndex = section.language_versions.findIndex(lv => lv === matchingVersion);
          const legacyGlobalIndex = legacyBaseIndex + (langIndex >= 0 ? langIndex : 0);
          const stableSectionKey = section.id || section.section_id || section.section_title || sectionIndex;
          
          sections.push({
            sectionTitle: getTranslatedSectionTitle(section),
            langVersion: matchingVersion,
            globalIndex: `section:${stableSectionKey}`,
            legacyGlobalIndex,
            sectionIndex: sectionIndex,
            isStandalone: false
          });
        }
      });
    }
    
    return sections;
  };

  const isSectionViewed = (section, sectionsSet = viewedSections) => {
    if (!section || !sectionsSet) return false;
    return sectionsSet.has(section.globalIndex) || sectionsSet.has(section.legacyGlobalIndex);
  };

  const isSectionAdaptivelyCompleted = (section) => {
    if (!section) return false;
    return adaptiveCompletedSections.has(section.globalIndex) || adaptiveCompletedSections.has(section.legacyGlobalIndex);
  };

  // Check if a section is locked (sequential progression for first-time attempts)
  const isSectionLocked = (index, filteredSections) => {
    // If topic is already completed, allow free navigation
    if (currentStatus === 'completed' || backendLoadedStatus === 'completed') return false;
    // First section is always unlocked
    if (index === 0) return false;
    // Subchapter N is locked unless section N-1 has been viewed/completed (including adaptive completion)
    const prevSection = filteredSections[index - 1];
    if (!isSectionViewed(prevSection) && !isSectionAdaptivelyCompleted(prevSection)) return true;
    return false;
  };

  // Navigate to next section
  const fetchPreAssessmentNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      // Add timestamp to prevent caching and ensure fresh data
      const response = await axios.get(`${API}/assessments/pre-assessment-notes?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('[Pre-Assessment] Fetched notes from backend:', response.data);
      
      // Store the content field from backend (not instruction_text)
      if (response.data && response.data.content !== undefined) {
        setPreAssessmentNotesContent(response.data.content || '');
        console.log('[Pre-Assessment] Content loaded:', response.data.content ? 'Custom content' : 'Empty content');
        return true;
      }
      
      // If no data structure, set empty string
      setPreAssessmentNotesContent('');
      console.log('[Pre-Assessment] No content structure found, using empty string');
      return true;
    } catch (error) {
      console.error('[Pre-Assessment] Failed to fetch notes:', error);
      setPreAssessmentNotesContent('');
      return false;
    }
  };

  const goToNextSection = (skipLockCheck = false) => {
    // Sub-chapter-wise: never auto-advance while a pre/post assessment overlay is active.
    if (scwPhaseRef.current) return false;
    const filteredSections = getFilteredSections();
    console.log('[Navigation] Current section index:', currentSectionIndex);
    console.log('[Navigation] Total filtered sections:', filteredSections.length);
    console.log('[Navigation] Has assessment:', !!topicAssessment);
    
    if (currentSectionIndex < filteredSections.length - 1) {
      const nextIndex = currentSectionIndex + 1;
      const nextSection = filteredSections[nextIndex];
      console.log('[Navigation] Moving to next section:', nextIndex, nextSection.sectionTitle);
      // Route through handleSectionClick so adaptive questions are checked
      handleSectionClick(nextSection, nextIndex, filteredSections, skipLockCheck);
      return true;
    } else {
      // Last section completed — check if assessment is needed (always, regardless of adaptive path)
      if (topicAssessment) {
        const assessmentPassed = assessmentAttempts.some(
          attempt => attempt.passed || attempt.score_percentage >= (topicAssessment?.pass_score_percentage || 80)
        );
        if (!assessmentPassed) {
          console.log('[Navigation] All sections complete — routing to assessment');
          setTimeout(async () => {
            await fetchPreAssessmentNotes();
            setShowPreAssessmentNotes(true);
          }, 500);
          return false;
        }
      }
      console.log('[Navigation] All sections complete');
      return false;
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContent(response.data);
      console.log('[TrainingContentModal] Content fetched, average_feedback_rating:', response.data.average_feedback_rating);
    } catch (error) {
      console.error('[TrainingContentModal] Failed to fetch content:', error);
      // Only show error if this is the initial load
      if (loading) {
        toast.error('Failed to fetch content details');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if this topic is fully adaptive-ready.
  // This is always evaluated for staff/manager so adaptive-ready chapters launch correctly.
  const fetchAdaptiveReadyStatus = async () => {
    const role = getResolvedUserRole();
    if (!isStaffOrManager(role) || !contentId) {
      setIsTopicAdaptiveReady(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API}/assessments/adaptive-ready/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ready = resp.data?.adaptive_ready === true;
      setIsTopicAdaptiveReady(ready);
      console.log('[Adaptive Ready]', contentId, ready ? '✅ Topic is adaptive-ready' : '❌ Topic NOT adaptive-ready, falling back to conventional', resp.data?.failing_sections || resp.data?.reason);
    } catch (error) {
      console.log('[Adaptive Ready] Check failed, defaulting to conventional:', error.message);
      setIsTopicAdaptiveReady(false);
    }
  };

  const fetchTopicAssessment = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('=== [Assessment Fetch] START ===');
      console.log('[Assessment Fetch] Topic ID:', contentId);
      console.log('[Assessment Fetch] User role:', user?.role);
      
      // Try to fetch questionnaire-based assessment first
      try {
        console.log('[Assessment Fetch] Trying questionnaire endpoint: /assessments/topic/' + contentId);
        const questionnaireResponse = await axios.get(`${API}/assessments/topic/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('[Assessment Fetch] Questionnaire response:', JSON.stringify(questionnaireResponse.data, null, 2));
        
        if (questionnaireResponse.data && questionnaireResponse.data.has_assessment) {
          console.log('[Assessment Fetch] ✅ Found questionnaire-based assessment');
          console.log('[Assessment Fetch] Questionnaire ID:', questionnaireResponse.data.questionnaire?.questionnaire_id);
          setTopicAssessment(questionnaireResponse.data.questionnaire);
          setAssessmentFetchDone(true);
          if (isStaffOrManager(user?.role)) {
            fetchAssessmentAttempts(questionnaireResponse.data.questionnaire.questionnaire_id);
          }
          console.log('=== [Assessment Fetch] END - Questionnaire found ===');
          return;
        }
      } catch (err) {
        console.log('[Assessment Fetch] ❌ No questionnaire found:', err.message);
        console.log('[Assessment Fetch] Trying direct questions endpoint...');
      }
      
      // If no questionnaire, try to fetch questions directly tagged to this chapter
      console.log('[Assessment Fetch] Trying direct questions endpoint: /assessments/questions/topic/' + contentId);
      const questionsResponse = await axios.get(`${API}/assessments/questions/topic/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Assessment Fetch] Direct questions response:', JSON.stringify(questionsResponse.data, null, 2));
      
      if (questionsResponse.data && questionsResponse.data.has_questions) {
        console.log('[Assessment Fetch] ✅ Found', questionsResponse.data.questions.length, 'direct questions');
        // Format questions as a pseudo-questionnaire for display
        const directAssessment = {
          questionnaire_id: `topic_${contentId}`,
          title: 'Chapter Assessment',
          description: 'Assessment questions for this chapter',
          question_ids: questionsResponse.data.questions.map(q => q.question_id),
          questions: questionsResponse.data.questions,
          pass_score_percentage: 80,
          is_direct_questions: true // Flag to indicate these are direct questions, not a questionnaire
        };
        setTopicAssessment(directAssessment);
        console.log('[Assessment Fetch] Successfully set direct assessment');
        console.log('[Assessment Fetch] Questions:', directAssessment.questions.map(q => ({
          id: q.question_id,
          text: q.question_text,
          type: q.question_type
        })));
      } else {
        console.log('[Assessment Fetch] ⚠️ No questions found - has_questions is false or missing');
        setTopicAssessment(null);
      }
      console.log('=== [Assessment Fetch] END ===');
      setAssessmentFetchDone(true);
    } catch (error) {
      console.error('[Assessment Fetch] ❌ Error fetching assessment:', error);
      console.error('[Assessment Fetch] Error details:', error.response?.data);
      setTopicAssessment(null);
      setAssessmentFetchDone(true);
    }
  };

  const checkAssessmentPresenceInline = async () => {
    try {
      const token = localStorage.getItem('token');

      try {
        const questionnaireResponse = await axios.get(`${API}/assessments/topic/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (questionnaireResponse.data?.has_assessment && questionnaireResponse.data?.questionnaire) {
          const questionnaire = questionnaireResponse.data.questionnaire;
          setTopicAssessment(questionnaire);
          setAssessmentFetchDone(true);
          if (isStaffOrManager(user?.role)) {
            fetchAssessmentAttempts(questionnaire.questionnaire_id);
          }
          return questionnaire;
        }
      } catch (error) {
        console.log('[Assessment Inline Check] Questionnaire check failed:', error.message);
      }

      const questionsResponse = await axios.get(`${API}/assessments/questions/topic/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (questionsResponse.data?.has_questions && questionsResponse.data.questions?.length > 0) {
        const directAssessment = {
          questionnaire_id: `topic_${contentId}`,
          title: 'Chapter Assessment',
          description: 'Assessment questions for this chapter',
          question_ids: questionsResponse.data.questions.map(q => q.question_id),
          questions: questionsResponse.data.questions,
          pass_score_percentage: 80,
          is_direct_questions: true
        };
        setTopicAssessment(directAssessment);
        setAssessmentFetchDone(true);
        if (isStaffOrManager(user?.role)) {
          fetchAssessmentAttempts(directAssessment.questionnaire_id);
        }
        return directAssessment;
      }

      setAssessmentFetchDone(true);
      return null;
    } catch (error) {
      console.log('[Assessment Inline Check] Failed:', error.message);
      setAssessmentFetchDone(true);
      return null;
    }
  };

  const fetchTopicFeedback = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use trainingTopicId if available, otherwise fall back to contentId
      const topicIdForFeedback = trainingTopicId || contentId;
      console.log('[Feedback] Fetching feedback for training_topic_id:', topicIdForFeedback);
      
      const response = await axios.get(`${API}/feedback/topic/${topicIdForFeedback}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Feedback] API response:', response.data);
      
      if (response.data.has_feedback) {
        // Check if already submitted
        if (response.data.already_submitted) {
          console.log('[Feedback] User already submitted feedback for this chapter');
          setTopicFeedback(null); // Don't show feedback again
          return;
        }
        
        // Handle both old feedback_set format and new direct questions format
        if (response.data.feedback_set) {
          console.log('[Feedback] Using feedback_set format');
          setTopicFeedback(response.data.feedback_set);
        } else if (response.data.questions) {
          console.log('[Feedback] Using direct questions format, converting...');
          // Convert direct questions to feedback_set format
          const feedbackSet = {
            feedback_set_id: `topic_${topicIdForFeedback}`,
            title: 'Training Feedback',
            description: 'Please share your feedback on this training',
            questions: response.data.questions
          };
          setTopicFeedback(feedbackSet);
        }
      } else {
        console.log('[Feedback] No feedback configured for this chapter');
      }
    } catch (error) {
      console.error('[Feedback] Error fetching feedback:', error);
    }
  };

  const fetchAssessmentAttempts = async (questionnaireId) => {
    try {
      console.log('[Assessment Attempts] Fetching attempts for questionnaire:', questionnaireId);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/attempts/user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[Assessment Attempts] All attempts:', response.data.length);
      const attempts = response.data.filter(a => a.questionnaire_id === questionnaireId);
      console.log('[Assessment Attempts] Filtered attempts for this questionnaire:', attempts.length);
      
      if (attempts.length > 0) {
        const maxScore = Math.max(...attempts.map(a => a.score_percentage));
        console.log('[Assessment Attempts] Best score:', maxScore);
        console.log('[Assessment Attempts] Has passed:', maxScore >= 80);
      }
      
      setAssessmentAttempts(attempts);
    } catch (error) {
      console.error('[Assessment Attempts] Error fetching attempts:', error);
      setAssessmentAttempts([]);
    }
  };

  // Wrapper: in Sub-chapter-wise mode, completing a sub-chapter's content must trigger the
  // POST assessment (must pass) BEFORE the section is finalized/unlocked. Returns true if it
  // deferred to the post-assessment (so callers must not auto-advance).
  const markSectionViewed = async (sectionIndex) => {
    if (isSubchapterWise()
        && scwPreDoneRef.current.has(sectionIndex)
        && !scwPassedRef.current.has(sectionIndex)) {
      // Content is complete — persist so a refresh/re-login resumes at the POST assessment
      scwContentDoneRef.current.add(sectionIndex);
      saveScwProgress();
      openScwPost(sectionIndex);
      return true; // deferred — do NOT finalize/advance yet
    }
    await finalizeSectionViewed(sectionIndex);
    return false;
  };

  const finalizeSectionViewed = async (sectionIndex) => {
    const newViewedSections = new Set(viewedSections);
    newViewedSections.add(sectionIndex);
    setViewedSections(newViewedSections);
    saveViewedSections(newViewedSections);

    // Get filtered sections for selected language
    const filteredSections = getFilteredSections();
    const viewedFilteredCount = filteredSections.filter(s => isSectionViewed(s, newViewedSections)).length;
    const totalFilteredSections = filteredSections.length;
    const allFilteredSectionsViewed = viewedFilteredCount === totalFilteredSections && totalFilteredSections > 0;
    
    // Check assessment status.
    // Sub-chapter-wise mode has NO final chapter assessment — completion is reached when all
    // sub-chapters (each gated by its own post-assessment) are done. So ignore any chapter
    // questionnaire for progress purposes in this mode.
    let assessmentForProgress = isSubchapterWise() ? null : topicAssessment;
    if (!isSubchapterWise() && !assessmentForProgress && !assessmentFetchDone && allFilteredSectionsViewed) {
      assessmentForProgress = await checkAssessmentPresenceInline();
    }
    const hasAssessment = !!assessmentForProgress;
    const passScore = assessmentForProgress?.pass_score_percentage || 80;
    const assessmentPassed = assessmentAttempts.some(attempt => attempt.passed || attempt.score_percentage >= passScore);
    
    // Detect adaptive mode — after adaptive failure, chapter assessment is required
    const isAdaptive = getEffectiveTrainingMode() === 'adaptive' && !adaptiveFailedSession;

    // Calculate progress considering both sections and assessment
    // Use filtered counts to only consider sections in selected language
    const newProgress = calculateProgress(
      { size: viewedFilteredCount },
      totalFilteredSections,
      hasAssessment,
      assessmentPassed,
      isAdaptive
    );

    const newStatus = determineStatus(newProgress, hasAssessment, assessmentPassed, isAdaptive);

    console.log('[Mark Subchapter Viewed] Section:', sectionIndex, 'Progress:', newProgress, 'Status:', newStatus);
    
    // Pass newViewedSections to ensure backend gets updated data
    await updateProgress(newStatus, newProgress, newViewedSections);
    localStorage.setItem('refreshDashboard', 'true');
  };

  // ===== Sub-chapter-wise assessment helpers =====
  const fetchScwQuestions = async (topicId, sectionId, sectionKey) => {
    if (sectionKey && scwQuestionsCacheRef.current[sectionKey]) {
      return scwQuestionsCacheRef.current[sectionKey];
    }
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(
        `${API}/assessments/questions/subchapter/${topicId}/${sectionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const qs = (resp.data?.questions || []).filter(q => q && q.question_id);
      if (sectionKey) scwQuestionsCacheRef.current[sectionKey] = qs;
      return qs;
    } catch (e) {
      console.error('[SCW] Failed to fetch sub-chapter questions:', e);
      return [];
    }
  };

  const pickScwQuestions = (all) => {
    const pool = [...(all || [])];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool.slice(0, Math.min(10, pool.length));
  };

  const gradeScw = (questions, answers) => {
    let correct = 0;
    const total = questions.length;
    questions.forEach(q => {
      const a = answers[q.question_id];
      if (q.question_type === 'mcq_single') {
        const correctOpt = (q.options || []).find(o => o.is_correct);
        if (a && correctOpt && a === correctOpt.option_id) correct++;
      } else if (q.question_type === 'mcq_multiple') {
        const correctIds = (q.options || []).filter(o => o.is_correct).map(o => o.option_id).sort();
        const given = Array.isArray(a) ? [...a].sort() : [];
        if (correctIds.length && given.length === correctIds.length && given.every((v, i) => v === correctIds[i])) correct++;
      } else if (q.question_type === 'ranking') {
        const given = Array.isArray(a) ? a : [];
        if (q.correct_order && given.length === q.correct_order.length && given.every((v, i) => v === q.correct_order[i])) correct++;
      }
    });
    return total > 0 ? Math.round((correct / total) * 100) : 0;
  };

  const openScwPre = (section, index, sectionId, questions) => {
    scwSectionRef.current = { section, index, sectionId, key: section.globalIndex };
    setScwQuestions(pickScwQuestions(questions));
    setScwIndex(0); setScwAnswers({}); setScwResult(null);
    setScwSubmittedIds({}); setScwShowScore(false);
    setScwPhase('pre'); scwPhaseRef.current = 'pre';
  };

  const openScwPost = (sectionKey) => {
    const ctx = scwSectionRef.current;
    const questions = scwQuestionsCacheRef.current[sectionKey]
      || (ctx && scwQuestionsCacheRef.current[ctx.key]) || [];
    setScwQuestions(pickScwQuestions(questions));
    setScwIndex(0); setScwAnswers({}); setScwResult(null);
    setScwSubmittedIds({}); setScwShowScore(false);
    setScwPhase('post'); scwPhaseRef.current = 'post';
  };

  const setScwAnswer = (q, value) => {
    // Locked once submitted
    if (scwSubmittedIds[q.question_id]) return;
    setScwAnswers(prev => ({ ...prev, [q.question_id]: value }));
  };

  // Evaluate a single question's submitted answer (for POST per-question feedback).
  const scwEvaluateQuestion = (q) => {
    const a = scwAnswers[q.question_id];
    let correct = false;
    if (q.question_type === 'mcq_single') {
      const c = (q.options || []).find(o => o.is_correct);
      correct = !!(a && c && a === c.option_id);
    } else if (q.question_type === 'mcq_multiple') {
      const ids = (q.options || []).filter(o => o.is_correct).map(o => o.option_id).sort();
      const g = Array.isArray(a) ? [...a].sort() : [];
      correct = ids.length > 0 && g.length === ids.length && g.every((v, i) => v === ids[i]);
    } else if (q.question_type === 'ranking') {
      const g = Array.isArray(a) ? a : [];
      correct = !!q.correct_order && g.length === q.correct_order.length && g.every((v, i) => v === q.correct_order[i]);
    }
    const msg = correct
      ? (q.right_instruction_translations?.[selectedLanguage] || q.right_instruction || '')
      : (q.wrong_instruction_translations?.[selectedLanguage] || q.wrong_instruction || '');
    return { correct, msg };
  };

  // Per-question Submit (CTA on every question). Locks the question; POST reveals correctness+feedback.
  const submitScwQuestion = () => {
    const q = scwQuestions[scwIndex];
    if (!q) return;
    setScwSubmittedIds(prev => ({ ...prev, [q.question_id]: true }));
  };

  // Advance after a question has been submitted.
  const scwNextQuestion = async () => {
    if (scwIndex < scwQuestions.length - 1) {
      setScwIndex(i => i + 1);
      return;
    }
    // Last question submitted
    if (scwPhase === 'pre') {
      setScwShowScore(true); // show the PRE score screen (no pass/fail)
    } else {
      await submitScwPost(); // grade POST vs institution % and show result
    }
  };

  // PRE: after the score screen, record the pre attempt and open the sub-chapter content.
  const scwContinueToContent = async () => {
    const ctx = scwSectionRef.current;
    if (!ctx) return;
    setScwSubmitting(true);
    const score = gradeScw(scwQuestions, scwAnswers);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assessments/subchapter-attempt`,
        { topic_id: content?.id, section_id: ctx.sectionId, phase: 'pre', score_percentage: score },
        { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) { console.error('[SCW] pre attempt record failed', e); }
    scwPreDoneRef.current.add(ctx.key);
    saveScwProgress();
    setScwSubmitting(false);
    setScwShowScore(false);
    setScwPhase(null); scwPhaseRef.current = null;
    // Open the sub-chapter content (pre is done, so the intercept falls through to content)
    handleSectionClick(ctx.section, ctx.index, getFilteredSections(), true);
  };

  const submitScwPost = async () => {
    const ctx = scwSectionRef.current;
    if (!ctx) return;
    setScwSubmitting(true);
    const score = gradeScw(scwQuestions, scwAnswers);
    let passed = score >= (resolvedPassingPct || 80);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API}/assessments/subchapter-attempt`,
        { topic_id: content?.id, section_id: ctx.sectionId, phase: 'post', score_percentage: score },
        { headers: { Authorization: `Bearer ${token}` } });
      if (typeof resp.data?.passed === 'boolean') passed = resp.data.passed;
    } catch (e) { console.error('[SCW] post attempt record failed', e); }
    if (passed) scwPassedRef.current.add(ctx.key);
    setScwResult({ score, passed });
    setScwSubmitting(false);
  };

  const scwContinueAfterPass = async () => {
    const ctx = scwSectionRef.current;
    const idx = ctx ? ctx.index : currentSectionIndex;
    setScwPhase(null); scwPhaseRef.current = null;
    setScwResult(null);
    if (ctx) {
      await finalizeSectionViewed(ctx.key); // mark sub-chapter complete
    }
    // Bug fix: advance to the NEXT sub-chapter so it starts with its OWN pre-assessment.
    const filteredSections = getFilteredSections();
    if (idx < filteredSections.length - 1) {
      const nextIndex = idx + 1;
      handleSectionClick(filteredSections[nextIndex], nextIndex, filteredSections, true);
    }
    // If it was the last sub-chapter, the chapter is now complete (no final chapter assessment in this mode).
  };

  const scwRetryPost = () => {
    const ctx = scwSectionRef.current;
    setScwIndex(0); setScwAnswers({}); setScwResult(null);
    setScwSubmittedIds({}); setScwShowScore(false);
    const questions = ctx ? (scwQuestionsCacheRef.current[ctx.key] || []) : [];
    setScwQuestions(pickScwQuestions(questions));
  };

  const updateProgress = async (status, percentage, sectionsToSave = null) => {
    try {
      const token = localStorage.getItem('token');
      // Use provided sections or fall back to current viewedSections
      const sectionsArray = sectionsToSave ? Array.from(sectionsToSave) : Array.from(viewedSections);
      
      await axios.put(`${API}/staff/progress/update`, {
        training_topic_id: contentId,
        status: status,
        completion_percentage: percentage,
        viewed_sections: sectionsArray // Save viewed sections array
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Progress tracked silently — no popup
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  };

  const isEmbeddedVideo = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com');
  };

  const getEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const handleView = (langVersion, sectionIndex, sectionTitle) => {
    // DO NOT mark section as viewed on open — completion is based on:
    // - Video: ended event or 90%+ watched
    // - PDF/Image: user clicks "Next Section" or "Mark Complete" button
    
    // Normalize field names - use file_url from backend (contains signed URL for S3 content)
    const normalizedVersion = {
      ...langVersion,
      file_url: langVersion.file_url || langVersion.file_name, // Preserve signed URL
      content_type: langVersion.file_type || langVersion.content_type,
      sectionIndex,
      sectionTitle
    };
    
    setActivePreview(normalizedVersion);
    setShowPreAssessmentNotes(false);
    
    if (normalizedVersion.content_type === 'pdf') {
      pdfViewStartTime.current = Date.now();
    }
  };

  // Adaptive section handler: check for adaptive questions before showing content
  const handleSectionClick = async (section, index, filteredSections, skipLockCheck = false) => {
    const resolvedUserRole = getResolvedUserRole();

    if (!skipLockCheck) {
      const isLocked = isStaffOrManager(resolvedUserRole) && isSectionLocked(index, filteredSections);
      if (isLocked) {
        toast.warning('Please complete the current section to unlock the next section.');
        return;
      }
    }

    // Reset adaptive states
    setAdaptiveMode(false);
    setAdaptiveSubmitted(false);
    setAdaptiveScore(null);
    setShowSummaryView(false);
    setAdaptiveInstructionMsg(null);
    setAdaptiveWrongAnswer(false);
    setShowAdaptiveReview(false);
    setAdaptiveRevisit(false);
    setShowKeyHighlightsCompletion(false);
    setShowAdaptiveInstructions(false);
    setShowCompletionReview(false);
    setViewingFullModuleFromAdaptive(false);

    // ===== Sub-chapter-wise (Conventional): resume from the LAST COMPLETED step =====
    //   pre not done            → Pre-assessment
    //   pre done, content not   → Content (fall through)
    //   pre + content done      → Post-assessment
    //   post passed (viewed)    → not intercepted (content available for review)
    // Sub-chapters with no questions skip assessments entirely.
    if (isSubchapterWise() && !scwPassedRef.current.has(section.globalIndex)) {
      const sectionData = content?.sections?.[section.sectionIndex];
      const sectionId = sectionData?.id || sectionData?.section_title;
      const key = section.globalIndex;
      if (content?.id && sectionId && !isSectionViewed(section)) {
        const questions = await fetchScwQuestions(content.id, sectionId, key);
        if (questions.length > 0) {
          // Establish the flow context for this sub-chapter (needed by the later POST step too)
          scwSectionRef.current = { section, index, sectionId, key };
          setCurrentSectionIndex(index);
          if (!scwPreDoneRef.current.has(key)) {
            openScwPre(section, index, sectionId, questions);
            return;
          }
          if (scwContentDoneRef.current.has(key)) {
            // Pre + content already done → resume directly at the post-assessment
            openScwPost(key);
            return;
          }
          // Pre done but content not finished → resume at the content (fall through below)
        }
        // No questions authored for this sub-chapter → skip assessments, proceed to content
      }
    }

    // If this chapter was previously completed via adaptive success, always land on Great Job screen
    // (do NOT auto-play the video and do NOT show the regular completion screen)
    if (isStaffOrManager(resolvedUserRole) && isSectionAdaptivelyCompleted(section)) {
      // Store section metadata for summary content
      if (content?.sections?.[section.sectionIndex]) {
        setCurrentSectionMeta(content.sections[section.sectionIndex]);
      }
      const normalizedVersion = {
        ...section.langVersion,
        file_url: section.langVersion.file_url || section.langVersion.file_name,
        content_type: section.langVersion.file_type || section.langVersion.content_type,
        sectionIndex: section.globalIndex,
        sectionTitle: section.sectionTitle
      };
      setActivePreview(normalizedVersion);
      setCurrentSectionIndex(index);
      setAdaptiveMode(true);
      setAdaptiveRevisit(true);
      setAdaptiveSubmitted(true);
      setAdaptiveScore(prev => prev ?? 100);
      setAdaptiveSuccessPath(true);
      // Lazy-load adaptive questions so the "Preview your Answers" review still works
      const sectionData = content?.sections?.[section.sectionIndex];
      const sectionId = sectionData?.id || sectionData?.section_title;
      if (content?.id && sectionId) {
        try {
          const token = localStorage.getItem('token');
          const resp = await axios.get(
            `${API}/assessments/questions/section/${content.id}/${sectionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (resp.data?.has_questions && resp.data.questions?.length > 0) {
            setAdaptiveSectionQuestions(resp.data.questions);
            setAdaptiveCorrectCount(resp.data.questions.length);
          }
        } catch (e) { /* non-critical */ }
      }
      return;
    }

    // If topic is completed (assessment passed), show per-section completion view
    // BUG FIX: Only show completion view if THIS specific section has actually been viewed.
    // After a topic reset (e.g. by Hospital Admin) viewedSections will be empty, so we
    // fall through to the regular learning/video flow instead of falsely showing "Completed".
    if (
      isStaffOrManager(resolvedUserRole) &&
      (currentStatus === 'completed' || backendLoadedStatus === 'completed') &&
      viewedSections.size > 0 &&
      isSectionViewed(section)
    ) {
      // Completed section clicked — dismiss completion screen and show content for review
      setShowCompletionScreen(false);
      setCompletionScreenDismissed(true);
      handleView(section.langVersion, section.globalIndex, section.sectionTitle);
      setCurrentSectionIndex(index);
      return;
    }

    // Store section metadata for summary content
    if (content?.sections?.[section.sectionIndex]) {
      setCurrentSectionMeta(content.sections[section.sectionIndex]);
    }

    // For staff/manager, check if this section has adaptive questions
    // Each section always starts with adaptive question check (even after previous section failures)
    // Only check when user has selected Adaptive Learning mode AND topic is adaptive-ready
    if (isStaffOrManager(resolvedUserRole) && content?.id && getEffectiveTrainingMode() === 'adaptive') {
      const sectionData = content.sections?.[section.sectionIndex];
      const sectionId = sectionData?.id || sectionData?.section_title;
      const sectionAlreadyCompleted = isSectionViewed(section);
      
      if (sectionId) {
        try {
          const token = localStorage.getItem('token');
          const resp = await axios.get(
            `${API}/assessments/questions/section/${content.id}/${sectionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (resp.data?.has_questions && resp.data.questions.length > 0) {
            const questions = resp.data.questions;
            setAdaptiveSectionQuestions(questions);
            setAdaptiveCurrentIndex(0);
            setAdaptiveAnswers({});
            setAdaptiveAnsweredSet(new Set());
            setAdaptiveInstructionMsg(null);
            setAdaptiveWrongAnswer(false);
            setAdaptiveMode(true);
            setCurrentSectionIndex(index);
            // Set activePreview to section info but DON'T play video yet
            const normalizedVersion = {
              ...section.langVersion,
              file_url: section.langVersion.file_url || section.langVersion.file_name,
              content_type: section.langVersion.file_type || section.langVersion.content_type,
              sectionIndex: section.globalIndex,
              sectionTitle: section.sectionTitle
            };
            setActivePreview(normalizedVersion);

            // If section is already completed, show completion screen directly
            if (sectionAlreadyCompleted) {
              setAdaptiveRevisit(true);
              setAdaptiveSubmitted(true);
              setAdaptiveScore(100);
              setAdaptiveCorrectCount(questions.length);
              return;
            }

            // First-time visit — show instructions for first section, skip for subsequent
            setAdaptiveSubmitted(false);
            setAdaptiveScore(null);
            setAdaptiveCorrectCount(0);
            if (index === 0) {
              setShowAdaptiveInstructions(true);
            }
            return;
          } else if (index === 0 && viewedSections.size === 0) {
            // First section, topic not yet started — silently fallback to conventional
            console.log('[Adaptive] Topic not ready for adaptive learning - no sequence-based questions, proceeding silently');
            handleView(section.langVersion, section.globalIndex, section.sectionTitle);
            setCurrentSectionIndex(index);
            return;
          }
        } catch (err) {
          console.log('[Adaptive] No adaptive questions for section:', err.message);
          if (index === 0 && viewedSections.size === 0) {
            // Silently fallback to conventional
            handleView(section.langVersion, section.globalIndex, section.sectionTitle);
            setCurrentSectionIndex(index);
            return;
          }
        }
      }
    }

    // No adaptive questions — proceed with conventional learning
    handleView(section.langVersion, section.globalIndex, section.sectionTitle);
    setCurrentSectionIndex(index);
  };

  // Handle adaptive answer selection
  const handleAdaptiveAnswer = (questionId, answer, isMultiple = false) => {
    if (isMultiple) {
      setAdaptiveAnswers(prev => {
        const current = prev[questionId] || [];
        const updated = current.includes(answer) ? current.filter(a => a !== answer) : [...current, answer];
        return { ...prev, [questionId]: updated };
      });
    } else {
      setAdaptiveAnswers(prev => ({ ...prev, [questionId]: answer }));
    }
  };

  // Handle adaptive Next with instruction display (mirrors conventional handleNextQuestion)
  const handleAdaptiveNext = () => {
    const q = adaptiveSectionQuestions[adaptiveCurrentIndex];
    const answer = adaptiveAnswers[q.question_id];
    if (!answer || adaptiveInstructionMsg) return;

    let isCorrect = false;
    if (q.question_type === 'mcq_single') {
      const selectedOpt = q.options?.find(o => o.option_id === answer);
      isCorrect = selectedOpt?.is_correct || false;
    } else if (q.question_type === 'mcq_multiple') {
      const correctIds = (q.options || []).filter(o => o.is_correct).map(o => o.option_id).sort();
      const answerIds = (answer || []).slice().sort();
      isCorrect = correctIds.length === answerIds.length && correctIds.every((id, i) => id === answerIds[i]);
    }

    const rightInstruction = q.right_instruction_translations?.[selectedLanguage] || q.right_instruction;
    const wrongInstruction = q.wrong_instruction_translations?.[selectedLanguage] || q.wrong_instruction;

    if (isCorrect && rightInstruction) {
      setAdaptiveInstructionMsg({ type: 'correct', message: rightInstruction });
    } else if (isCorrect) {
      setAdaptiveInstructionMsg({ type: 'correct', message: 'Your answer is correct!' });
    } else {
      // Wrong answer — show full-canvas encouraging message
      setAdaptiveWrongAnswer(true);
    }
  };

  // After adaptive instruction shown, move to next question or submit
  const handleAdaptiveContinueAfterInstruction = () => {
    setAdaptiveInstructionMsg(null);
    if (adaptiveCurrentIndex >= adaptiveSectionQuestions.length - 1) {
      handleAdaptiveSubmit();
    } else {
      setAdaptiveCurrentIndex(i => i + 1);
    }
  };

  // Submit adaptive section questions and calculate score
  const handleAdaptiveSubmit = async () => {
    let correct = 0;
    const total = adaptiveSectionQuestions.length;
    
    adaptiveSectionQuestions.forEach(q => {
      const answer = adaptiveAnswers[q.question_id];
      if (!answer) return;
      
      if (q.question_type === 'mcq_single') {
        const correctOpt = q.options?.find(o => o.is_correct);
        if (correctOpt && answer === correctOpt.option_id) correct++;
      } else if (q.question_type === 'mcq_multiple') {
        const correctIds = (q.options || []).filter(o => o.is_correct).map(o => o.option_id).sort();
        const answerIds = (answer || []).sort();
        if (JSON.stringify(correctIds) === JSON.stringify(answerIds)) correct++;
      } else if (['ranking', 'arrange'].includes(q.question_type)) {
        const correctOrder = q.correct_order || q.options?.map(o => o.option_id);
        if (correctOrder && JSON.stringify(answer) === JSON.stringify(correctOrder)) correct++;
      }
    });
    
    const score = total > 0 ? Math.round((correct / total) * 100) : 0;
    setAdaptiveScore(score);
    setAdaptiveCorrectCount(correct);
    setAdaptiveSubmitted(true);

    // Remember this section was completed via adaptive so future revisits land on Great Job screen
    if (score >= 80 && activePreview?.sectionIndex !== undefined) {
      // Trigger celebration when reaching the Great Job screen
      triggerSubchapterCelebration(activePreview.sectionIndex);
      markSectionAdaptivelyCompleted(activePreview.sectionIndex);

      // For the last subchapter, sync progress. If no adaptive failures occurred, auto-complete
      // the assessment and mark 100%. If any failure occurred, cap at 80% until the user takes
      // the required chapter assessment.
      const filteredSections = getFilteredSections();
      const isLast = currentSectionIndex === filteredSections.length - 1;
      if (isLast) {
        const sectionIndex = activePreview.sectionIndex;
        // Mark section as viewed
        if (!viewedSections.has(sectionIndex)) {
          await markSectionViewed(sectionIndex);
        }
        const newViewedSections = new Set(viewedSections);
        newViewedSections.add(sectionIndex);
        const viewedFilteredCount = filteredSections.filter(s => isSectionViewed(s, newViewedSections)).length;

        if (!adaptiveFailedSession) {
          // Pure adaptive completion — auto-complete assessment and mark 100%
          if (topicAssessment) {
            try {
              const token = localStorage.getItem('token');
              await axios.post(`${API}/assessments/adaptive-complete`, {
                questionnaire_id: topicAssessment.questionnaire_id,
                training_topic_id: contentId
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              await fetchAssessmentAttempts(topicAssessment.questionnaire_id);
            } catch (error) {
              console.error('[Adaptive Complete] Error auto-completing assessment:', error);
            }
          }
          const newProgress = calculateProgress(
            { size: viewedFilteredCount },
            filteredSections.length,
            !!topicAssessment,
            true,
            true // adaptive mode
          );
          const newStatus = determineStatus(newProgress, !!topicAssessment, true, true);
          await updateProgress(newStatus, newProgress, newViewedSections);
          localStorage.setItem('refreshDashboard', 'true');
          console.log('[Adaptive] Last subchapter passed — chapter marked 100% complete');
          triggerChapterCelebration(contentId);
        } else {
          // Adaptive fallback occurred earlier — chapter assessment required. Cap at 80%.
          const newProgress = calculateProgress(
            { size: viewedFilteredCount },
            filteredSections.length,
            !!topicAssessment,
            false, // assessment NOT yet passed
            false  // treat as conventional for progress
          );
          const newStatus = determineStatus(newProgress, !!topicAssessment, false, false);
          await updateProgress(newStatus, newProgress, newViewedSections);
          localStorage.setItem('refreshDashboard', 'true');
          console.log('[Adaptive] Last subchapter passed but earlier failure — chapter assessment required, capped at 80%');
        }
      }
    }
  };

  // After adaptive pass: proceed to full content
  const handleWatchFullModule = () => {
    // Re-invoke handleView to properly set up the content player with correct signed URL
    if (activePreview) {
      const filteredSections = getFilteredSections();
      const section = filteredSections[currentSectionIndex];
      if (section) {
        handleView(section.langVersion, section.globalIndex, section.sectionTitle);
      }
    }
    setAdaptiveMode(false);
    setShowSummaryView(false);
    setShowAdaptiveReview(false);
    setShowKeyHighlightsCompletion(false);
    setShowAdaptiveInstructions(false);
    // Keep adaptive results in memory so user can return via "Back to Summary"
    setViewingFullModuleFromAdaptive(true);
    setAdaptiveSuccessPath(true);
  };

  // From full module view, return to the adaptive "Great Job" summary screen
  const handleBackToAdaptiveSummary = () => {
    setViewingFullModuleFromAdaptive(false);
    setAdaptiveMode(true);
    setAdaptiveSubmitted(true);
    setShowSummaryView(false);
    setShowAdaptiveReview(false);
    setShowKeyHighlightsCompletion(false);
    setShowAdaptiveInstructions(false);
    setAdaptiveSuccessPath(true);
    if (adaptiveScore === null) setAdaptiveScore(100);
  };

  // After adaptive pass: show key highlights in main content area
  const handleViewSummary = () => {
    setAdaptiveMode(false);
    setAdaptiveSubmitted(false);
    setShowSummaryView(true);
    setShowAdaptiveReview(false);
    setAdaptiveRevisit(false);
    setShowKeyHighlightsCompletion(false);
    setShowAdaptiveInstructions(false);
    setAdaptiveSuccessPath(true); // User passed adaptive questions — skip regular assessment
  };

  // After adaptive fail: go to full content
  const handleGoToTraining = () => {
    // Re-invoke handleView to properly set up the content player
    if (activePreview) {
      const filteredSections = getFilteredSections();
      const section = filteredSections[currentSectionIndex];
      if (section) {
        handleView(section.langVersion, section.globalIndex, section.sectionTitle);
      }
    }
    setAdaptiveMode(false);
    setAdaptiveSubmitted(false);
    setAdaptiveWrongAnswer(false);
    setShowSummaryView(false);
    setShowAdaptiveReview(false);
    setAdaptiveRevisit(false);
    setShowKeyHighlightsCompletion(false);
    setShowAdaptiveInstructions(false);
    setAdaptiveSuccessPath(false); // User failed adaptive — regular assessment applies
    setAdaptiveFailedSession(true); // Prevent adaptive questions for remaining sections and require chapter assessment
    try {
      const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
      localStorage.setItem(`adaptive_failed_${userId}_${contentId}`, 'true');
    } catch (e) { /* ignore */ }
    // Persist fallback server-side: removes any adaptive_complete shortcut record,
    // caps progress at 80%, and requires the chapter assessment before completion.
    // This keeps the popup, dashboard listing, and unlock logic in sync.
    (async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/staff/adaptive-fallback`, {
          training_topic_id: contentId
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (e) {
        console.error('[Adaptive Fallback] Failed to persist fallback state:', e);
      }
    })();
  };

  // Handle finishing last section in adaptive success path (Key Highlights completion)
  const handleLastSectionFinish = async () => {
    const sectionIndex = activePreview?.sectionIndex;
    if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
      await markSectionViewed(sectionIndex);
    }

    // Detect if user is in adaptive learning mode
    const isAdaptiveUser = getEffectiveTrainingMode() === 'adaptive';

    // In adaptive mode, skip final assessment ONLY if user passed all adaptive questions
    // (no failures). If user failed any subchapter adaptive questions and watched full
    // content, fall through to conventional assessment flow.
    if (isAdaptiveUser && !adaptiveFailedSession) {
      console.log('[handleLastSectionFinish] Adaptive mode, no failures — skipping final assessment');
      // Auto-complete assessment record if one exists
      if (topicAssessment) {
        try {
          const token = localStorage.getItem('token');
          await axios.post(`${API}/assessments/adaptive-complete`, {
            questionnaire_id: topicAssessment.questionnaire_id,
            training_topic_id: contentId
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          await fetchAssessmentAttempts(topicAssessment.questionnaire_id);
        } catch (error) {
          console.error('[Adaptive Complete] Error auto-completing assessment:', error);
        }
      }
      // Mark 100% completion
      const filteredSections = getFilteredSections();
      const newViewedSections = new Set(viewedSections);
      if (sectionIndex !== undefined) newViewedSections.add(sectionIndex);
      const viewedFilteredCount = filteredSections.filter(s => isSectionViewed(s, newViewedSections)).length;
      const newProgress = calculateProgress(
        { size: viewedFilteredCount },
        filteredSections.length,
        !!topicAssessment,
        true,
        true // adaptive mode - 100% from subchapters
      );
      const newStatus = determineStatus(newProgress, !!topicAssessment, true, true);
      await updateProgress(newStatus, newProgress, newViewedSections);
      localStorage.setItem('refreshDashboard', 'true');
      setShowSummaryView(true);
      setShowKeyHighlightsCompletion(true);
      triggerChapterCelebration(contentId);
      return;
    }
    if (isAdaptiveUser && adaptiveFailedSession) {
      console.log('[handleLastSectionFinish] Adaptive mode but user failed subchapter — requiring chapter assessment');
    }

    // === Conventional mode below ===

    // If assessment fetch hasn't completed yet, wait for it
    // This prevents bypassing assessment due to race condition
    if (!assessmentFetchDone && !topicAssessment) {
      console.log('[handleLastSectionFinish] Assessment fetch not done, triggering fresh fetch...');
      try {
        const token = localStorage.getItem('token');
        // Quick check: does this chapter have questions?
        const resp = await axios.get(`${API}/assessments/questions/topic/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (resp.data?.has_questions && resp.data.questions?.length > 0) {
          // Has assessment — redirect to it
          console.log('[handleLastSectionFinish] Found assessment via inline check — redirecting');
          await fetchPreAssessmentNotes();
          setShowPreAssessmentNotes(true);
          return;
        }
      } catch (e) {
        console.log('[handleLastSectionFinish] Inline assessment check failed:', e.message);
      }
    }

    if (topicAssessment) {
      // Assessment exists — redirect to assessment if not passed
      const assessmentAlreadyPassed = assessmentAttempts.some(
        attempt => attempt.passed || attempt.score_percentage >= (topicAssessment?.pass_score_percentage || 80)
      );
      if (!assessmentAlreadyPassed) {
        console.log('[Last Section] Redirecting to assessment (assessment not yet passed)');
        await fetchPreAssessmentNotes();
        setShowPreAssessmentNotes(true);
        return; // Don't show completion screen — go to assessment instead
      }
    }

    setShowSummaryView(true);
    setShowKeyHighlightsCompletion(true);
  };

  const handleVideoProgress = async (e) => {
    const video = e.target;
    const watchedPercentage = (video.currentTime / video.duration) * 100;
    
    // Sub-chapter-wise: do NOT complete/assess while the video is still playing — wait for the
    // actual 'ended' event (handled in handleVideoEnded). Other modes keep the 95% behaviour.
    if (isStaffOrManager(user?.role) && watchedPercentage >= 95 && !isSubchapterWise()) {
      const sectionIndex = activePreview?.sectionIndex || 0;
      if (!viewedSections.has(sectionIndex)) {
        await markSectionViewed(sectionIndex);
      }
    }
  };

  const handleVideoEnded = async () => {
    console.log('[Video] Video ended event fired');
    console.log('[Video] Current section index:', currentSectionIndex);
    console.log('[Video] Active preview:', activePreview);

    // Mark that the video has fully ended — enables the fallback "Continue to Assessment" CTA
    setVideoEnded(true);

    // Mark current section as viewed if not already
    const sectionIndex = activePreview?.sectionIndex || 0;
    if (isStaffOrManager(user?.role) && !viewedSections.has(sectionIndex)) {
      console.log('[Video] Marking section as viewed:', sectionIndex);
      await markSectionViewed(sectionIndex);
    }

    // Sub-chapter-wise: if the POST assessment was just launched, stop here (do not auto-advance/complete)
    if (scwPhaseRef.current === 'post') return;

    // Check if this is the last section
    const filteredSections = getFilteredSections();
    const isLastSection = currentSectionIndex >= filteredSections.length - 1;
    
    if (isLastSection && isStaffOrManager(user?.role)) {
      // In adaptive mode with no prior failures, skip final assessment — go directly to completion.
      // If any subchapter failed adaptively, fall through to conventional assessment flow.
      if (getEffectiveTrainingMode() === 'adaptive' && !adaptiveFailedSession) {
        console.log('[Video] Adaptive mode (no failures) — skipping final assessment, completing chapter');
        handleLastSectionFinish();
        return;
      }

      // Conventional: Last section completed — check for assessment
      let hasAssessment = !!topicAssessment;
      
      if (!hasAssessment && !assessmentFetchDone) {
        // Assessment fetch still in progress — do an inline check
        try {
          const token = localStorage.getItem('token');
          const resp = await axios.get(`${API}/assessments/questions/topic/${contentId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.data?.has_questions && resp.data.questions?.length > 0) {
            hasAssessment = true;
            console.log('[Video] Found assessment via inline check');
          }
        } catch (e) {
          console.log('[Video] Inline assessment check failed:', e.message);
        }
      }
      
      if (hasAssessment) {
        console.log('[Video] Last section completed — auto-navigating to assessment');
        setTimeout(async () => {
          await handleTakeAssessmentClick();
        }, 500);
        return;
      }
    }
    
    if (!isLastSection) {
      // Not the last section — advance to next section
      console.log('[Video] Advancing to next section');
      setTimeout(() => {
        goToNextSection(true);
      }, 1000);
    } else {
      console.log('[Video] Last section completed, topic complete');
      // Call handleLastSectionFinish for last section without assessment
      handleLastSectionFinish();
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video':
        return <Play className="w-4 h-4" />;
      case 'pdf':
        return <FileText className="w-4 h-4" />;
      case 'image':
        return <ImageIcon className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const handleStartAssessmentFromNotes = () => {
    setShowPreAssessmentNotes(false);
    handleTakeAssessment();
  };

  const handleTakeAssessmentClick = async () => {
    // Check if user has already passed
    const assessmentPassed = assessmentAttempts.some(attempt => attempt.passed || attempt.score_percentage >= 80);
    
    if (assessmentPassed) {
      // User already passed, directly start assessment in review mode
      console.log('[Assessment] User already passed, going to review mode');
      handleTakeAssessment();
    } else {
      // User hasn't passed, show Pre-Assessment Notes first
      console.log('[Assessment] User hasn\'t passed, fetching latest Pre-Assessment Notes');
      // Always fetch fresh content from backend before showing
      await fetchPreAssessmentNotes();
      setShowPreAssessmentNotes(true);
    }
  };

  // Helper function to randomly select up to 10 questions
  const selectRandomQuestions = (questions, maxCount = 10) => {
    if (questions.length <= maxCount) {
      return questions; // Return all if 10 or fewer
    }
    
    // Shuffle array and take first 10
    const shuffled = [...questions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, maxCount);
  };

  const handleTakeAssessment = async () => {
    try {
      // Check if we already have questions (direct questions from topic)
      if (topicAssessment.is_direct_questions && topicAssessment.questions) {
        // Use questions already loaded from direct topic query
        console.log('[Assessment] Using direct questions:', topicAssessment.questions.length);
        const selectedQuestions = selectRandomQuestions(topicAssessment.questions, 10);
        console.log('[Assessment] Selected random questions:', selectedQuestions.length);
        setAssessmentQuestions(selectedQuestions);
        setShowAssessment(true);
        setShowPreAssessmentNotes(false);
        setCurrentAnswers({});
        setAssessmentSubmitted(false);
        setAssessmentScore(null);
        setCurrentQuestionIndex(0);
        setCurrentInstructionMessage(null); // Reset instruction display
        setAnsweredQuestions(new Set()); // Reset answered tracking
        return;
      }
      
      // Otherwise, fetch questionnaire details (questionnaire-based assessment)
      console.log('[Assessment] Fetching questionnaire details');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/questionnaires/${topicAssessment.questionnaire_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[Assessment] Loaded questionnaire questions:', response.data.question_details?.length);
      const selectedQuestions = selectRandomQuestions(response.data.question_details || [], 10);
      console.log('[Assessment] Selected random questions:', selectedQuestions.length);
      setAssessmentQuestions(selectedQuestions);
      setShowAssessment(true);
      setShowPreAssessmentNotes(false);
      setCurrentAnswers({});
      setAssessmentSubmitted(false);
      setAssessmentScore(null);
      setCurrentQuestionIndex(0);
      setCurrentInstructionMessage(null); // Reset instruction display
      setAnsweredQuestions(new Set()); // Reset answered tracking
    } catch (error) {
      console.error('Assessment load error:', error);
      toast.error('Failed to load assessment');
    }
  };

  const handleAnswerChange = (questionId, answer, isMultiple = false) => {
    if (isMultiple) {
      // For checkboxes - toggle selection
      setCurrentAnswers(prev => {
        const current = prev[questionId] || [];
        const updated = current.includes(answer)
          ? current.filter(a => a !== answer)
          : [...current, answer];
        return { ...prev, [questionId]: updated };
      });
    } else {
      // For radio buttons - single selection
      setCurrentAnswers(prev => ({
        ...prev,
        [questionId]: answer
      }));
    }
  };

  // Handle moving to next question with instruction display
  const handleNextQuestion = () => {
    const currentQuestion = assessmentQuestions[currentQuestionIndex];
    const currentAnswer = currentAnswers[currentQuestion.question_id];
    
    // Check if user has answered this question and hasn't seen feedback yet
    if (currentAnswer && !currentInstructionMessage) {
      // Determine if answer is correct
      let isCorrect = false;
      if (currentQuestion.question_type === 'mcq_single') {
        // Single choice - check if selected option is correct
        const selectedOption = currentQuestion.options.find(opt => opt.option_id === currentAnswer);
        isCorrect = selectedOption?.is_correct || false;
      } else if (currentQuestion.question_type === 'mcq_multiple') {
        // Multiple choice - check if all selected are correct and all correct are selected
        const selectedOptions = currentAnswer || [];
        const correctOptions = currentQuestion.options.filter(opt => opt.is_correct).map(opt => opt.option_id);
        isCorrect = selectedOptions.length === correctOptions.length && 
                    selectedOptions.every(opt => correctOptions.includes(opt)) &&
                    correctOptions.every(opt => selectedOptions.includes(opt));
      }
      
      // Always show feedback if instruction is available (works for first attempt and reattempts)
      // Use multilingual instructions if available, fallback to single language instruction
      const rightInstruction = currentQuestion.right_instruction_translations?.[selectedLanguage] 
        || currentQuestion.right_instruction;
      const wrongInstruction = currentQuestion.wrong_instruction_translations?.[selectedLanguage] 
        || currentQuestion.wrong_instruction;
      
      if (isCorrect && rightInstruction) {
        setCurrentInstructionMessage({
          type: 'correct',
          message: rightInstruction
        });
      } else if (!isCorrect && wrongInstruction) {
        setCurrentInstructionMessage({
          type: 'incorrect',
          message: wrongInstruction
        });
      } else {
        // No instruction available, show default message or move to next
        if (isCorrect) {
          setCurrentInstructionMessage({
            type: 'correct',
            message: 'Your answer is correct!'
          });
        } else {
          setCurrentInstructionMessage({
            type: 'incorrect',
            message: 'Your answer is incorrect. Please review the content.'
          });
        }
      }
      
      // Mark as answered
      setAnsweredQuestions(prev => new Set([...prev, currentQuestion.question_id]));
    } else if (currentAnswer) {
      // Answer exists but feedback already shown, just move to next
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentInstructionMessage(null);
    } else {
      // No answer, just move to next
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentInstructionMessage(null);
    }
  };

  // Clear instruction and move to next question (after instruction is shown)
  const handleContinueAfterInstruction = () => {
    setCurrentInstructionMessage(null);
    
    // Check if this is the last question
    if (currentQuestionIndex === assessmentQuestions.length - 1) {
      // Last question - submit assessment instead of moving to next
      handleSubmitAssessment();
    } else {
      // Not last question - move to next
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleSubmitAssessment = async () => {
    try {
      setIsSubmittingAssessment(true);
      const token = localStorage.getItem('token');
      
      // Convert answers to backend format
      const formattedAnswers = assessmentQuestions.map(question => {
        const answer = currentAnswers[question.question_id];
        
        if (question.question_type === 'ranking') {
          // Convert rankings object to arranged order
          const rankingEntries = Object.entries(answer || {});
          const sortedEntries = rankingEntries.sort((a, b) => a[1] - b[1]);
          return {
            question_id: question.question_id,
            question_type: question.question_type,
            arranged_order: sortedEntries.map(entry => entry[0])
          };
        } else if (question.question_type === 'mcq_multiple') {
          // Multiple choice - array of option_ids
          return {
            question_id: question.question_id,
            question_type: question.question_type,
            selected_options: answer || []
          };
        } else {
          // Single MCQ and True/False - single option_id in array
          return {
            question_id: question.question_id,
            question_type: question.question_type,
            selected_options: answer ? [answer] : []
          };
        }
      });
      
      const response = await axios.post(`${API}/assessments/submit`, {
        questionnaire_id: topicAssessment.questionnaire_id,
        answers: formattedAnswers
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssessmentScore(response.data.score_percentage);
      
      // Calculate correct answers if backend doesn't provide (fallback)
      let correctCount = response.data.correct_count;
      if (correctCount === undefined || correctCount === null) {
        // Calculate from score percentage
        const totalQuestions = response.data.total_questions || assessmentQuestions.length;
        correctCount = Math.round((response.data.score_percentage / 100) * totalQuestions);
      }
      
      setCorrectAnswersCount(correctCount);
      setTotalQuestionsCount(response.data.total_questions || assessmentQuestions.length);
      setAssessmentSubmitted(true);
      const assessmentPassed = response.data.score_percentage >= (topicAssessment?.pass_score_percentage || 80);
      
      if (assessmentPassed) {
        toast.success(`🎉 Assessment passed! Score: ${response.data.score_percentage}%`);
        // Trigger chapter completion celebration on assessment result screen
        triggerChapterCelebration(contentId);
        // Clear adaptive failed flag since chapter is now complete
        try {
          const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}').id;
          localStorage.removeItem(`adaptive_failed_${userId}_${contentId}`);
        } catch (e) { /* ignore */ }
        setAdaptiveFailedSession(false);
      } else {
        toast.info(`Assessment completed. Score: ${response.data.score_percentage}%. You can retake to improve.`);
      }
      
      // Refresh attempts
      await fetchAssessmentAttempts(topicAssessment.questionnaire_id);
      
      // Update topic status and progress after assessment
      // Get filtered sections for selected language
      const filteredSections = getFilteredSections();
      const viewedFilteredCount = filteredSections.filter(s => isSectionViewed(s)).length;
      const totalFilteredSections = filteredSections.length;
      
      // Recalculate progress including assessment result
      // Use filtered counts to only consider sections in selected language
      const newProgress = calculateProgress(
        { size: viewedFilteredCount },
        totalFilteredSections,
        true,
        assessmentPassed,
        false // conventional assessment path
      );
      const newStatus = determineStatus(newProgress, true, assessmentPassed, false);
      
      await updateProgress(newStatus, newProgress);
      localStorage.setItem('refreshDashboard', 'true');

      // If assessment passed, check if feedback needs to be collected
      if (assessmentPassed) {
        // Re-fetch feedback to get latest status
        try {
          const token = localStorage.getItem('token');
          const feedbackResponse = await axios.get(`${API}/feedback/topic/${contentId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('[TrainingContentModal] Feedback check after passing assessment:', feedbackResponse.data);
          
          // If feedback exists for this chapter and not submitted yet
          if (feedbackResponse.data.has_feedback && !feedbackResponse.data.already_submitted) {
            console.log('[TrainingContentModal] ✅ Feedback configured and not submitted');
            console.log('[TrainingContentModal] Feedback will be shown when you close this window');
            
            // Just store the feedback data, will be shown when modal closes
            if (feedbackResponse.data.feedback_set) {
              setTopicFeedback(feedbackResponse.data.feedback_set);
            } else if (feedbackResponse.data.questions) {
              setTopicFeedback({
                feedback_set_id: `topic_${contentId}`,
                title: 'Training Feedback',
                description: 'Please share your feedback on this training',
                questions: feedbackResponse.data.questions
              });
            }
            
            toast.success('🎉 Assessment passed! Please close this window to provide your feedback.');
          } else {
            console.log('[TrainingContentModal] No feedback configured for this chapter or already submitted');
          }
        } catch (error) {
          console.log('[TrainingContentModal] Error fetching feedback:', error);
        }
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setIsSubmittingAssessment(false);
    }
  };

  const handleRetakeAssessment = () => {
    setCurrentAnswers({});
    setAssessmentSubmitted(false);
    setAssessmentScore(null);
    setCurrentQuestionIndex(0);
    setCurrentInstructionMessage(null); // Reset instruction messages for reattempt
    setCorrectAnswersCount(0);
    setTotalQuestionsCount(0);
  };

  // Handle modal close - check feedback status dynamically every time
  const handleModalClose = async (open) => {
    if (!open) {
      setShowMobileSubchapters(false);
      // Modal is being closed
      console.log('======================================');
      console.log('[TrainingContentModal] 🔴 MODAL CLOSING - Checking feedback status');
      console.log('[TrainingContentModal] Debug Info:', {
        contentId,
        trainingTopicId,
        currentProgress,
        currentStatus,
        isStaffOrManager: isStaffOrManager(user?.role),
        hasAssessment: !!topicAssessment,
        assessmentAttempts: assessmentAttempts.length
      });
      console.log('======================================');
      
      // CRITICAL FIX: For Online topics with assessment, verify assessment is actually passed
      // before triggering feedback. This prevents feedback from appearing prematurely.
      const hasAssessment = !!topicAssessment;
      const assessmentPassed = assessmentAttempts.some(attempt => attempt.passed || attempt.score_percentage >= 80);
      
      // Only trigger feedback if:
      // 1. User is staff/manager
      // 2. Status is completed AND progress is 100%
      // 3. If topic has assessment, assessment must be passed
      // Also trigger when showKeyHighlightsCompletion is true (just completed via adaptive/conventional finish)
      const isTrulyComplete = (currentStatus === 'completed' && currentProgress === 100 && 
                              (!hasAssessment || assessmentPassed)) || showKeyHighlightsCompletion;
      
      // Check if this is a staff/manager user and topic is truly 100% completed
      if (isStaffOrManager(user?.role) && isTrulyComplete) {
        try {
          console.log('[TrainingContentModal] ✅ Topic is truly 100% completed (assessment verified), checking feedback status...');
          
          // Fetch latest feedback status from backend
          const token = localStorage.getItem('token');
          const topicIdForFeedback = getTopicIdForFeedback();
          
          if (!topicIdForFeedback) {
            console.error('[TrainingContentModal] ❌ No valid topic ID for feedback check');
            onClose();
            return;
          }
          
          const feedbackResponse = await axios.get(`${API}/feedback/topic/${topicIdForFeedback}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('[TrainingContentModal] Feedback response:', feedbackResponse.data);
          
          // Check if feedback exists and is NOT submitted yet
          if (feedbackResponse.data.has_feedback && !feedbackResponse.data.already_submitted) {
            console.log('[TrainingContentModal] 🎯 Feedback exists and NOT submitted - Passing to parent!');
            
            // Set feedback data
            const feedbackSet = feedbackResponse.data.feedback_set || {
              feedback_set_id: `topic_${topicIdForFeedback}`,
              title: 'Training Feedback',
              description: 'Please share your feedback on this training',
              questions: feedbackResponse.data.questions || []
            };
            
            // Prepare feedback data to pass to parent
            const feedbackData = {
              topicId: topicIdForFeedback,
              topicName: getTranslatedChapterTitle(),
              feedbackSet: feedbackSet,
              isMandatory: true
            };
            
            // Close training modal and pass feedback data to parent
            console.log('[TrainingContentModal] Closing modal and passing feedback data to parent');
            onClose(feedbackData);
            
            return; // Don't continue with normal close
          } else if (feedbackResponse.data.already_submitted) {
            console.log('[TrainingContentModal] ✅ Feedback already submitted - Normal close');
          } else {
            console.log('[TrainingContentModal] ℹ️ No feedback configured for this chapter - Normal close');
          }
        } catch (error) {
          console.error('[TrainingContentModal] ❌ Error checking feedback status:', error);
        }
      } else {
        console.log('[TrainingContentModal] ℹ️ Topic not completed or not staff user - Normal close');
      }
      
      // Normal close without feedback
      onClose();
    }
  };

  const handleFeedbackSubmit = async () => {
    try {
      setShowFeedbackModal(false);
      setTopicFeedback(null);
      localStorage.setItem('refreshDashboard', 'true');
      
      // Refresh the content to show updated average rating
      console.log('[TrainingContentModal] Refreshing content after feedback submission');
      await fetchContent();
      console.log('[TrainingContentModal] Content refreshed successfully');
    } catch (error) {
      console.error('[TrainingContentModal] Error refreshing content after feedback:', error);
      // Don't show error toast here - feedback was already submitted successfully
    }
  };

  // Summary content player component
  const SummaryPlayer = ({ type, s3Key, fileName, onVideoEnded }) => {
    const [signedUrl, setSignedUrl] = useState(null);
    const [loadError, setLoadError] = useState(false);
    
    useEffect(() => {
      if (!s3Key) return;
      
      // If the URL is already signed (starts with http), use it directly
      if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
        setSignedUrl(s3Key);
        return;
      }
      
      // Otherwise fetch a signed URL from the backend
      const fetchUrl = async () => {
        try {
          const token = localStorage.getItem('token');
          const resp = await axios.post(`${API}/s3/signed-url`, { s3_key: s3Key, expiration: 3600 }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.data?.signed_url) setSignedUrl(resp.data.signed_url);
          else setLoadError(true);
        } catch { setLoadError(true); }
      };
      fetchUrl();
    }, [s3Key]);
    
    if (loadError) return <p className="text-sm text-gray-500">Key Highlights content unavailable</p>;
    if (!signedUrl) return <div className="flex items-center justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div></div>;
    
    if (type === 'video') {
      return (
        <video controls autoPlay className="w-full h-full" src={signedUrl} onEnded={onVideoEnded}>
          Your browser does not support the video tag.
        </video>
      );
    }
    
    return (
      <iframe src={signedUrl} className="w-full h-full" title="Key Highlights PDF" />
    );
  };

  if (loading) {
    return (
      <>
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogContent className="training-learning-dialog max-w-[1400px] h-[90vh]" hideClose>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </DialogContent>
      </Dialog>
      </>
    );
  }

  if (!content) return null;

  const translatedChapterTitle = getTranslatedChapterTitle();
  const currentScwSectionTitle = scwSectionRef.current?.sectionTitle || activePreview?.sectionTitle || translatedChapterTitle;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={handleModalClose}>
      <DialogContent className="training-learning-dialog w-[90vw] md:max-w-[1400px] md:w-[95vw] h-[95vh] p-0 gap-0 bg-gray-50 overflow-hidden" hideClose>
        <DialogTitle className="sr-only">{translatedChapterTitle}</DialogTitle>

        {/* Adaptive Fallback Popup */}
        {showAdaptiveFallback && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-8 max-w-md mx-4 text-center">
              <div className="text-4xl mb-4">📋</div>
              <p className="text-gray-800 mb-2">
                <strong>This chapter is not ready for Adaptive Learning.</strong>
              </p>
              <p className="text-gray-600 text-sm mb-6">
                Please continue with the conventional learning flow.
              </p>
              <Button
                onClick={() => {
                  setShowAdaptiveFallback(false);
                  const pending = pendingFallbackSectionRef.current;
                  if (pending) {
                    handleView(pending.section.langVersion, pending.section.globalIndex, pending.section.sectionTitle);
                    setCurrentSectionIndex(pending.index);
                    pendingFallbackSectionRef.current = null;
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2"
              >
                Noted
              </Button>
            </div>
          </div>
        )}

        {/* Header Bar - 70% reduced height */}
        <div className="training-learning-header bg-gradient-to-r from-blue-600 to-blue-700 px-3 md:px-4 py-1 flex items-center justify-between relative min-w-0" style={{ height: '45px' }}>
          <div className="flex-1 min-w-0 pr-3 md:pr-20">
            <h2 className="training-learning-title text-sm md:text-base font-semibold text-white line-clamp-1 leading-tight break-words">{translatedChapterTitle}</h2>
          </div>
          
          {/* Progress Info for Staff */}
          {isStaffOrManager(user?.role) && (
            <div className="training-learning-progress hidden md:block mr-8 md:mr-10 shrink-0">
              <div className="relative p-1">
                <div className="w-9 h-9">
                  <svg className="w-9 h-9 transform -rotate-90" viewBox="0 0 36 36">
                    <circle
                      cx="18"
                      cy="18"
                      r="14.5"
                      stroke="white"
                      strokeWidth="2"
                      fill="transparent"
                      opacity="0.3"
                    />
                    <circle
                      cx="18"
                      cy="18"
                      r="14.5"
                      stroke="white"
                      strokeWidth="2"
                      fill="transparent"
                      strokeDasharray={`${currentProgress * 0.91} 91`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-white leading-none">{currentProgress}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Close Button - High-contrast red accent */}
          <button
            onClick={() => handleModalClose(false)}
            className="absolute top-1.5 right-2 z-50 w-9 h-9 flex items-center justify-center rounded-full bg-[#E53935] hover:bg-[#C62828] hover:scale-105 transition-all duration-200 cursor-pointer"
            style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.15)' }}
            aria-label="Close training window"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Two Column Layout - Content starts immediately after header - Stacks on mobile */}
        <div className="training-learning-body flex flex-col md:flex-row overflow-hidden min-w-0" style={{ height: 'calc(100% - 45px)' }}>
          {/* Left Column - Preview (Video/PDF) - Takes full width on mobile, appears first */}
          <div className="training-learning-main flex-1 bg-black flex flex-col relative min-w-0 h-[40vh] md:h-auto">
            {/* Conventional "Before You Begin" intro — rendered INSIDE the content workspace (not a pop-up) */}
            {showConventionalIntro ? (
              <div className="w-full h-full bg-gradient-to-br from-gray-50 to-blue-50 overflow-y-auto p-4 md:p-6 flex items-start md:items-center justify-center">
                <div className="bg-white rounded-2xl shadow-lg border border-blue-200 p-6 md:p-8 max-w-2xl w-full">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="bg-blue-100 p-2.5 rounded-xl">
                      <BookOpen className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Before You Begin</h3>
                  </div>
                  <p className="text-gray-600 mb-4">Here's how this learning flow works:</p>
                  <ul className="space-y-3 text-sm text-gray-700 mb-6">
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">•</span><span>You'll go through each sub-chapter of this chapter, one at a time.</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">•</span><span>Watch or read the content in each sub-chapter completely.</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">•</span><span>After completing each sub-chapter, you'll take a short assessment. You must pass the assessment to complete the sub-chapter and progress towards completing the chapter.</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">•</span><span>Your progress is saved automatically, so you can resume anytime.</span></li>
                    <li className="flex items-start gap-2"><span className="text-blue-500 mt-0.5">•</span><span>Please ensure a stable internet connection for a smooth learning experience.</span></li>
                  </ul>
                  <div className="flex justify-center">
                    <Button onClick={() => { try { localStorage.setItem(scwIntroKey(), '1'); } catch {} setShowConventionalIntro(false); }} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 text-base">Begin →</Button>
                  </div>
                </div>
              </div>
            ) : scwPhase ? (
              /* Sub-chapter Pre/Post assessment — rendered INSIDE the content workspace (not a pop-up) */
              <div className="training-assessment-scroll w-full h-full bg-gradient-to-br from-gray-50 to-blue-50 overflow-y-auto p-4 md:p-6">
                <div className="training-assessment-inner max-w-2xl mx-auto bg-white rounded-2xl shadow-md overflow-hidden">
                  <div className="px-6 py-4" style={{ background: 'linear-gradient(135deg, #0056B3 0%, #003d82 100%)' }}>
                    <h3 className="text-base md:text-lg font-bold text-white leading-snug break-words">
                      {currentScwSectionTitle}
                    </h3>
                    <p className="text-xs md:text-sm text-blue-100 mt-1 leading-relaxed">
                      {getScwAssessmentSupportText(scwPhase)}
                    </p>
                  </div>
                  <div className="training-question-content p-6">
                    {scwPhase === 'pre' && scwShowScore ? (
                      /* PRE score screen — no pass/fail, just the recorded score */
                      <div className="text-center py-4">
                        <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                          <ClipboardCheck className="w-9 h-9 text-[#0056B3]" />
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">Pre-Assessment Complete</h4>
                        <p className="text-3xl font-extrabold mt-2 text-[#0056B3]">{gradeScw(scwQuestions, scwAnswers)}%</p>
                        <p className="text-sm text-gray-600 mt-2">Your score is recorded for insights only — there is no passing requirement here.</p>
                        <div className="mt-6 flex justify-center">
                          <Button onClick={scwContinueToContent} disabled={scwSubmitting} className="bg-[#0056B3] hover:bg-[#004494] text-white px-8 py-2.5">
                            {scwSubmitting ? 'Loading…' : 'Continue to Content →'}
                          </Button>
                        </div>
                      </div>
                    ) : scwResult ? (
                      /* POST result screen — pass/fail vs institution % */
                      <div className="text-center py-4">
                        <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${scwResult.passed ? 'bg-emerald-100' : 'bg-red-100'}`}>
                          {scwResult.passed ? <CheckCircle className="w-9 h-9 text-emerald-600" /> : <X className="w-9 h-9 text-red-600" />}
                        </div>
                        <h4 className="text-xl font-bold text-gray-900">{scwResult.passed ? 'Passed!' : 'Not passed yet'}</h4>
                        <p className="text-3xl font-extrabold mt-2" style={{ color: scwResult.passed ? '#10B981' : '#EF4444' }}>{scwResult.score}%</p>
                        <p className="text-sm text-gray-600 mt-2">
                          {scwResult.passed ? 'You have completed this sub-chapter.' : `You need ${resolvedPassingPct || 80}% to pass. Please review the content and try again.`}
                        </p>
                        <div className="mt-6 flex justify-center">
                          {scwResult.passed ? (
                            <Button onClick={scwContinueAfterPass} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-2.5">Continue →</Button>
                          ) : (
                            <Button onClick={scwRetryPost} className="bg-[#0056B3] hover:bg-[#004494] text-white px-8 py-2.5">Retry Assessment</Button>
                          )}
                        </div>
                      </div>
                    ) : scwQuestions.length > 0 && scwQuestions[scwIndex] ? (
                      (() => {
                        const q = scwQuestions[scwIndex];
                        const a = scwAnswers[q.question_id];
                        const isMultiple = q.question_type === 'mcq_multiple';
                        const answered = isMultiple ? (Array.isArray(a) && a.length > 0) : !!a;
                        const submitted = !!scwSubmittedIds[q.question_id];
                        const isLast = scwIndex === scwQuestions.length - 1;
                        const evalR = (submitted && scwPhase === 'post') ? scwEvaluateQuestion(q) : null;
                        return (
                          <div className="min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 min-w-0">
                              <span className="text-sm font-semibold text-[#0056B3]">Question {scwIndex + 1} of {scwQuestions.length}</span>
                              <div className="w-full sm:w-40 bg-blue-100 rounded-full h-2">
                                <div className="bg-[#0056B3] h-2 rounded-full transition-all" style={{ width: `${((scwIndex + 1) / scwQuestions.length) * 100}%` }} />
                              </div>
                            </div>
                            <h4 className="training-question-text text-lg font-semibold text-gray-900 mb-4 break-words">{getTranslatedQuestionText(q)}</h4>
                            <div className="space-y-2">
                              {(q.options || []).map((opt) => {
                                const selected = isMultiple ? (Array.isArray(a) && a.includes(opt.option_id)) : a === opt.option_id;
                                let rowClass = selected ? 'border-[#0056B3] bg-blue-50' : 'border-gray-200 hover:border-gray-300';
                                let boxClass = selected ? 'bg-[#0056B3] border-[#0056B3]' : 'border-gray-300';
                                if (submitted) {
                                  rowClass = selected ? 'border-[#0056B3] bg-blue-50' : 'border-gray-200';
                                  if (scwPhase === 'post' && selected) {
                                    rowClass = evalR?.correct ? 'border-emerald-400 bg-emerald-50' : 'border-red-300 bg-red-50';
                                    boxClass = evalR?.correct ? 'bg-emerald-500 border-emerald-500' : 'bg-red-500 border-red-500';
                                  }
                                }
                                return (
                                  <button
                                    key={opt.option_id}
                                    disabled={submitted}
                                    onClick={() => {
                                      if (isMultiple) {
                                        const cur = Array.isArray(a) ? a : [];
                                        setScwAnswer(q, cur.includes(opt.option_id) ? cur.filter(x => x !== opt.option_id) : [...cur, opt.option_id]);
                                      } else {
                                        setScwAnswer(q, opt.option_id);
                                      }
                                    }}
                                    className={`training-answer-option w-full text-left px-4 py-3 rounded-lg border-2 transition-all flex items-start gap-3 min-w-0 ${rowClass} ${submitted ? 'cursor-default' : ''}`}
                                  >
                                    <span className={`training-answer-control training-answer-control-faux ${selected ? 'is-selected' : ''} w-5 h-5 ${isMultiple ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${boxClass}`}>
                                      {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                    </span>
                                    <span className="training-answer-text text-sm text-gray-800 flex-1 min-w-0 break-words leading-relaxed">{getTranslatedOptionText(opt)}</span>
                                  </button>
                                );
                              })}
                            </div>

                            {/* POST: correct/incorrect + configured feedback message for the selected answer */}
                            {submitted && scwPhase === 'post' && (
                              <div className={`mt-4 rounded-lg p-3 text-sm ${evalR?.correct ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                                <p className="font-semibold mb-1">{evalR?.correct ? 'Correct' : 'Incorrect'}</p>
                                {evalR?.msg ? <p className="leading-relaxed">{evalR.msg}</p> : null}
                              </div>
                            )}
                            {/* PRE: no correct/incorrect feedback — just confirm the answer was recorded */}
                            {submitted && scwPhase === 'pre' && (
                              <div className="mt-4 rounded-lg p-3 text-sm bg-blue-50 text-blue-800">Answer recorded.</div>
                            )}

                            <div className="training-question-actions mt-6 flex items-center justify-end gap-2">
                              {!submitted ? (
                                <Button disabled={!answered} onClick={submitScwQuestion} className="bg-[#0056B3] hover:bg-[#004494] text-white px-8">Submit</Button>
                              ) : (
                                <Button disabled={scwSubmitting} onClick={scwNextQuestion} className="bg-[#0056B3] hover:bg-[#004494] text-white px-8">
                                  {isLast ? (scwPhase === 'pre' ? 'View Score →' : (scwSubmitting ? 'Submitting…' : 'See Result →')) : 'Next Question →'}
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="text-center py-8 text-gray-600">No questions available.</div>
                    )}
                  </div>
                </div>
              </div>
            ) : showCompletionScreen ? (
              /* Section-wise Completion View - Shown on revisit of completed topic */
              <div className="w-full h-full flex flex-col bg-gray-50 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4">
                  {showCompletionReview ? (
                    /* Inline Answer Review - Adaptive Style */
                    <div className="training-assessment-inner max-w-2xl mx-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <ClipboardCheck className="w-5 h-5 text-blue-600" /> Answer Review
                        </h3>
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          onClick={() => setShowCompletionReview(false)}
                        >
                          ← Back to Results
                        </button>
                      </div>
                      <div className="space-y-4">
                        {assessmentQuestions.map((q, qIdx) => {
                          const userAnswer = currentAnswers[q.question_id];
                          return (
                            <div key={q.question_id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                              <div className="flex items-start gap-3 mb-3">
                                <span className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">{qIdx + 1}</span>
                                <p className="font-medium text-gray-900 text-sm leading-relaxed">{getTranslatedQuestionText(q)}</p>
                              </div>
                              <div className="ml-10 space-y-2">
                                {q.options?.map(opt => {
                                  const isCorrect = opt.is_correct;
                                  const isSelected = q.question_type === 'mcq_multiple'
                                    ? (userAnswer || []).includes(opt.option_id)
                                    : userAnswer === opt.option_id;
                                  
                                  let bgClass = 'bg-gray-50 border-gray-200';
                                  let iconEl = null;
                                  if (isCorrect) {
                                    bgClass = 'bg-green-50 border-green-300';
                                    iconEl = <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />;
                                  } else if (isSelected && !isCorrect) {
                                    bgClass = 'bg-red-50 border-red-300';
                                    iconEl = <X className="w-4 h-4 text-red-500 flex-shrink-0" />;
                                  }
                                  
                                  return (
                                    <div key={opt.option_id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${bgClass}`}>
                                      {iconEl || <div className="w-4 h-4 flex-shrink-0" />}
                                      <span className={`flex-1 ${isCorrect ? 'text-green-800 font-medium' : isSelected && !isCorrect ? 'text-red-800' : 'text-gray-700'}`}>
                                        {getTranslatedOptionText(opt)}
                                      </span>
                                      {isSelected && (
                                        <span className="text-xs text-gray-500 italic flex-shrink-0">Your answer</span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {(q.right_instruction || q.right_instruction_translations?.[selectedLanguage]) && (
                                <div className="ml-10 mt-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5">
                                  <p className="text-xs font-semibold text-blue-700 mb-1">Explanation</p>
                                  <p className="text-sm text-blue-800 leading-relaxed">{q.right_instruction_translations?.[selectedLanguage] || q.right_instruction}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Subchapter Completion Card */
                    <div className="w-full px-6 py-4 flex items-center justify-center min-h-full">
                      <div className="bg-white rounded-xl shadow-lg p-10 text-center border border-gray-200 w-full max-w-[90%]">
                        <div className="text-5xl mb-4">{isChapterAssessmentPending() ? '📋' : '✅'}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                          {isChapterAssessmentPending() ? 'All subchapters completed' : 'Chapter Completed'}
                        </h3>
                        <p className={`text-lg font-semibold mb-6 ${isChapterAssessmentPending() ? 'text-amber-700' : 'text-green-700'}`}>
                          {isChapterAssessmentPending() ? 'Chapter Assessment pending' : (content?.title || 'This chapter')}
                        </p>

                        {/* Assessment Results - shown if topic has assessment */}
                        {topicAssessment && assessmentAttempts.length > 0 && (() => {
                          const bestAttempt = assessmentAttempts.reduce((best, current) => 
                            current.score_percentage > best.score_percentage ? current : best
                          , assessmentAttempts[0]);
                          const totalQ = bestAttempt?.total_questions || bestAttempt?.answers?.length || 0;
                          const correctQ = bestAttempt?.correct_count || Math.round((bestAttempt?.score_percentage || 0) * totalQ / 100);
                          return totalQ > 0 ? (
                            <>
                              <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <span className="text-2xl font-bold text-blue-600">{totalQ}</span>
                                  <span className="text-xs text-blue-600 block mt-1">Questions</span>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                  <span className="text-2xl font-bold text-green-600">{correctQ}</span>
                                  <span className="text-xs text-green-600 block mt-1">Correct</span>
                                </div>
                                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                  <span className="text-2xl font-bold text-red-500">{totalQ - correctQ}</span>
                                  <span className="text-xs text-red-500 block mt-1">Wrong</span>
                                </div>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                                <div className="flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                                  <span className="text-sm text-green-700 font-medium">
                                    Assessment Passed — Score: {Math.max(...assessmentAttempts.map(a => a.score_percentage))}%
                                  </span>
                                </div>
                              </div>
                            </>
                          ) : null;
                        })()}

                        {/* No assessment - simple completion badge */}
                        {!topicAssessment && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                            <div className="flex items-center justify-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                              <span className="text-sm text-green-700 font-medium">All subchapters completed</span>
                            </div>
                          </div>
                        )}

                        {/* Time Saved - shown if adaptive completed sections exist */}
                        {adaptiveCompletedSections.size > 0 && (() => {
                          const totalTimeSaved = (content?.sections || []).reduce((total, sec, idx) => {
                            const filteredSections = getFilteredSections();
                            const matchingSection = filteredSections.find(fs => fs.sectionIndex === idx);
                            if (matchingSection && adaptiveCompletedSections.has(matchingSection.globalIndex) && sec.content_duration) {
                              return total + sec.content_duration;
                            }
                            return total;
                          }, 0);
                          return totalTimeSaved > 0 ? (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-6">
                              <div className="flex items-center justify-center">
                                <span className="text-sm text-purple-700 font-medium">
                                  🕐 You saved {totalTimeSaved} {totalTimeSaved === 1 ? 'minute' : 'minutes'} through adaptive learning
                                </span>
                              </div>
                            </div>
                          ) : null;
                        })()}

                        <div className="flex flex-col items-center space-y-3">
                          {/* Review Your Answers - loads inline in adaptive style */}
                          {topicAssessment && assessmentAttempts.length > 0 && (
                            <button
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1.5 transition-colors"
                              onClick={async () => {
                                try {
                                  const bestAttempt = assessmentAttempts.reduce((best, current) => 
                                    current.score_percentage > best.score_percentage ? current : best
                                  , assessmentAttempts[0]);
                                  
                                  const token = localStorage.getItem('token');
                                  const reviewResponse = await axios.get(
                                    `${API}/assessments/attempts/${bestAttempt.attempt_id}/review`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  
                                  const reviewQuestions = reviewResponse.data.questions.map(q => ({
                                    ...q,
                                    correct_options: q.options?.filter(opt => opt.is_correct).map(opt => opt.option_id) || []
                                  }));
                                  setAssessmentQuestions(reviewQuestions);
                                  
                                  const reviewAnswers = {};
                                  if (bestAttempt.answers) {
                                    bestAttempt.answers.forEach(ans => {
                                      if (ans.selected_options && ans.selected_options.length > 0) {
                                        reviewAnswers[ans.question_id] = ans.question_type === 'mcq_multiple' ? ans.selected_options : ans.selected_options[0];
                                      } else if (ans.selected_option) {
                                        reviewAnswers[ans.question_id] = ans.selected_option;
                                      }
                                    });
                                  }
                                  setCurrentAnswers(reviewAnswers);
                                  setShowCompletionReview(true);
                                } catch (error) {
                                  toast.error('Failed to load review');
                                }
                              }}
                            >
                              <ClipboardCheck className="w-4 h-4" /> Review Your Answers
                            </button>
                          )}

                          {/* View Key Highlights - shown if any section has summary content */}
                          {(() => {
                            const currentSection = content?.sections?.[getFilteredSections()[currentSectionIndex]?.sectionIndex];
                            const hasSummary = currentSection?.summary_video || currentSection?.summary_pdf;
                            if (!hasSummary) return null;
                            return (
                              <Button
                                onClick={() => {
                                  setCurrentSectionMeta(currentSection);
                                  setShowCompletionScreen(false);
                                  handleViewSummary();
                                }}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                View Key Highlights
                              </Button>
                            );
                          })()}

                          <Button
                            onClick={() => {
                              setShowCompletionScreen(false);
                              setCompletionScreenDismissed(true);
                            }}
                            variant="outline"
                            className="px-6"
                          >
                            Back to Content
                          </Button>

                          {isChapterAssessmentPending() ? (
                            <Button
                              onClick={async () => {
                                setShowCompletionScreen(false);
                                setAdaptiveMode(false);
                                setAdaptiveSubmitted(false);
                                await fetchPreAssessmentNotes();
                                setShowPreAssessmentNotes(true);
                              }}
                              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white px-6"
                            >
                              <ClipboardCheck className="w-4 h-4 mr-2" />
                              Go to Chapter Assessment
                            </Button>
                          ) : (
                            <Button
                              onClick={() => handleModalClose(false)}
                              className="bg-green-600 hover:bg-green-700 text-white px-6"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Go back to Training Dashboard
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : showPreAssessmentNotes && !showAssessment ? (
              /* Pre-Assessment Notes View - Inline in main content area */
              <div className="w-full h-full flex flex-col bg-gray-50 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="max-w-3xl mx-auto">
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg overflow-hidden">
                      <div className="flex items-center space-x-3 p-6 border-b border-yellow-200">
                        <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">Pre-Assessment Instructions</h3>
                          <p className="text-sm text-gray-600">Please read carefully before starting the assessment</p>
                        </div>
                      </div>
                      
                      <div className="bg-white p-6">
                        {preAssessmentNotesContent ? (
                          <div 
                            className="text-gray-700 leading-relaxed pre-assessment-content"
                            dangerouslySetInnerHTML={{ __html: preAssessmentNotesContent }}
                            style={{ fontSize: '15px', lineHeight: '1.6' }}
                          />
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm">No pre-assessment instructions have been configured yet.</p>
                            <p className="text-xs mt-2">Please proceed to start the assessment.</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-center space-x-3 p-4 bg-yellow-50 border-t border-yellow-200">
                        <Button
                          onClick={() => setShowPreAssessmentNotes(false)}
                          variant="outline"
                          className="px-6"
                        >
                          Back to Content
                        </Button>
                        <Button
                          onClick={handleStartAssessmentFromNotes}
                          className="px-8 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Start Assessment
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : showAssessment ? (
              /* Assessment View */
              <div className="w-full h-full flex flex-col bg-gray-50 overflow-hidden">
                {/* Assessment Header */}
                <div className="bg-gradient-to-r from-purple-900 to-indigo-900 px-4 py-2 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <GraduationCap className="w-5 h-5 text-purple-300" />
                      <div>
                        <h3 className="text-white font-medium text-sm">Chapter Assessment</h3>
                        <p className="text-purple-300 text-xs">Conventional Learning • {assessmentQuestions.length} Questions</p>
                      </div>
                    </div>
                    {!assessmentSubmitted && (
                      <button
                        onClick={() => setShowAssessment(false)}
                        className="text-white hover:bg-white/20 rounded-lg px-3 py-1.5 text-sm transition-colors"
                      >
                        Back to Content
                      </button>
                    )}
                  </div>
                </div>

                {/* Assessment Content - Scrollable Container with proper height */}
                <div className="training-assessment-scroll flex-1 bg-gradient-to-br from-gray-50 to-purple-50 overflow-y-auto min-h-0 p-4">
                  {assessmentSubmitted ? (
                    /* Results View */
                    <div className="training-assessment-inner max-w-2xl mx-auto px-6 py-8">
                      <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
                        {/* Hero Message */}
                        {assessmentScore >= (topicAssessment?.pass_score_percentage || 80) ? (
                          <>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Congratulations! 🎉</h3>
                            <p className="text-gray-600 mb-6">
                              You have successfully passed this assessment.
                            </p>

                            {/* Result Statistics */}
                            <div className="grid grid-cols-3 gap-3 mb-6">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-blue-600">{assessmentQuestions.length}</span>
                                <span className="text-xs text-blue-600 block mt-1">Questions</span>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-green-600">{correctAnswersCount}</span>
                                <span className="text-xs text-green-600 block mt-1">Correct</span>
                              </div>
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-red-500">{assessmentQuestions.length - correctAnswersCount}</span>
                                <span className="text-xs text-red-500 block mt-1">Wrong</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <h3 className="text-2xl font-bold text-red-600 mb-4">Assessment Not Passed</h3>
                            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
                              <p className="text-lg font-semibold text-gray-800 leading-relaxed">
                                Sorry! You have scored <span className="text-red-600 font-bold">{Math.round(assessmentScore)}%</span>, while the minimum passing score is <span className="text-red-600 font-bold">{topicAssessment?.pass_score_percentage || 80}%</span>.
                              </p>
                              <p className="text-base text-gray-700 mt-3">
                                Please try again to complete this chapter.
                              </p>
                            </div>
                          </>
                        )}
                        
                        <div className="flex flex-col items-center space-y-3">
                          {/* Review Answers Button - Only show when PASSED */}
                          {assessmentScore >= (topicAssessment?.pass_score_percentage || 80) && (
                            <Button
                              onClick={async () => {
                                try {
                                  console.log('[Review] Starting review from results screen');
                                  
                                  // Get the latest attempt that was just submitted
                                  if (!assessmentAttempts || assessmentAttempts.length === 0) {
                                    toast.error('Please wait while we load your assessment...');
                                    // Refresh attempts first
                                    await fetchAssessmentAttempts(topicAssessment.questionnaire_id);
                                    return;
                                  }
                                  
                                  // Get the most recent attempt (first in array since sorted by submitted_at desc)
                                  const latestAttempt = assessmentAttempts[0];
                                  console.log('[Review] Using latest attempt:', latestAttempt.attempt_id);
                                  
                                  // Fetch review data from backend
                                  const token = localStorage.getItem('token');
                                  const reviewResponse = await axios.get(
                                    `${API}/assessments/attempts/${latestAttempt.attempt_id}/review`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  
                                  console.log('[Review] Review data loaded:', reviewResponse.data);
                                  
                                  // Set review attempt
                                  setReviewAttempt(reviewResponse.data);
                                  
                                  // Format questions with correct_options
                                  const reviewQuestions = reviewResponse.data.questions.map(q => ({
                                    ...q,
                                    correct_options: q.options
                                      ?.filter(opt => opt.is_correct)
                                      .map(opt => opt.option_id) || []
                                  }));
                                  
                                  setAssessmentQuestions(reviewQuestions);
                                  
                                  // Set user's answers from the attempt
                                  const reviewAnswers = {};
                                  if (latestAttempt.answers) {
                                    latestAttempt.answers.forEach(ans => {
                                      // Handle different answer formats
                                      if (ans.selected_options && ans.selected_options.length > 0) {
                                        reviewAnswers[ans.question_id] = ans.question_type === 'mcq_multiple' 
                                          ? ans.selected_options 
                                          : ans.selected_options[0];
                                      } else if (ans.selected_option) {
                                        reviewAnswers[ans.question_id] = ans.selected_option;
                                      }
                                    });
                                  }
                                  setCurrentAnswers(reviewAnswers);
                                  
                                  // Enter review mode
                                  setIsReviewMode(true);
                                  setAssessmentSubmitted(false);
                                  setCurrentQuestionIndex(0);
                                  
                                  toast.info('📖 Reviewing your answers - slide through each question');
                                } catch (error) {
                                  console.error('[Review] Error:', error);
                                  toast.error(`Failed to load review: ${error.response?.data?.detail || error.message}`);
                                }
                              }}
                              variant="ghost"
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              <ClipboardCheck className="w-4 h-4 mr-1.5" />
                              Review Your Answers
                            </Button>
                          )}
                          
                          {/* After passing: Continue to Next Subchapter or Go to Dashboard */}
                          {assessmentScore >= (topicAssessment?.pass_score_percentage || 80) && (
                            currentSectionIndex < getFilteredSections().length - 1 ? (
                              <Button
                                onClick={() => {
                                  setShowAssessment(false);
                                  goToNextSection(true);
                                }}
                                className="w-full max-w-md bg-green-600 hover:bg-green-700 text-white font-semibold"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Continue to Next Subchapter →
                              </Button>
                            ) : (
                              <Button
                                onClick={() => {
                                  handleModalClose(false);
                                }}
                                className="w-full max-w-md bg-green-600 hover:bg-green-700 text-white font-semibold"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Go back to Training Dashboard
                              </Button>
                            )
                          )}
                          
                          <div className="flex items-center space-x-3 w-full max-w-md">
                            {/* Show Retake button if user failed - Make it prominent */}
                            {assessmentScore < (topicAssessment?.pass_score_percentage || 80) && (
                              <Button
                                onClick={handleRetakeAssessment}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              >
                                <Award className="w-4 h-4 mr-2" />
                                Retake Assessment
                              </Button>
                            )}
                            <Button
                              onClick={() => setShowAssessment(false)}
                              variant="outline"
                              className="flex-1"
                            >
                              Back to Content
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Single Question Per Page View - Optimized Layout */
                    <div className="training-assessment-inner max-w-2xl mx-auto">
                      {/* Review Mode Banner - Compact */}
                      {isReviewMode && (
                        <div className="mb-3 bg-gradient-to-r from-blue-600 to-indigo-600 border border-blue-700 p-3 rounded-lg shadow-md">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
                                <Award className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-white">📖 Answer Review Mode</p>
                                <p className="text-xs text-blue-100">
                                  Your Score: <span className="font-bold">{reviewAttempt?.score_percentage || assessmentScore}%</span>
                                  {(reviewAttempt?.passed || assessmentScore >= 80) && " • ✅ Passed"}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setIsReviewMode(false);
                                setShowAssessment(false);
                                toast.info('Exited review mode');
                              }}
                              className="text-white hover:bg-white/20 rounded-lg px-2 py-1 text-xs transition-colors"
                            >
                              Exit Review
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {assessmentQuestions.length > 0 && currentQuestionIndex < assessmentQuestions.length && (
                        <>
                          {!isReviewMode && (
                            <div className="mb-6">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-gray-900">Chapter Assessment</h3>
                                <span className="text-sm text-blue-600 font-medium">
                                  {currentQuestionIndex + 1} / {assessmentQuestions.length}
                                </span>
                              </div>
                              <div className="w-full bg-blue-100 rounded-full h-2">
                                <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${((currentQuestionIndex + 1) / assessmentQuestions.length) * 100}%` }} />
                              </div>
                            </div>
                          )}
                          {/* Current Question - Optimized Layout */}
                          {(() => {
                            const question = assessmentQuestions[currentQuestionIndex];
                            const hasAnswer = currentAnswers[question.question_id] !== undefined && 
                              (Array.isArray(currentAnswers[question.question_id]) 
                                ? currentAnswers[question.question_id].length > 0 
                                : currentAnswers[question.question_id] !== null);

                            return (
                              <div key={question.question_id} className="training-question-card relative bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                                {/* Question Content */}
                                <div className="training-question-content p-5 pb-2">
                                  <div className="flex items-start gap-2 mb-3 min-w-0">
                                    <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs">
                                      {currentQuestionIndex + 1}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="training-question-text text-lg font-semibold text-gray-900 mb-2 break-words">{getTranslatedQuestionText(question)}</h4>
                                      {question.question_image && (
                                        <img 
                                          src={question.question_image} 
                                          alt="Question" 
                                          className="w-full max-w-md rounded-lg mb-3 shadow-sm"
                                        />
                                      )}
                                    </div>
                                  </div>

                                  {/* MCQ Options */}
                                  {(question.question_type === 'mcq' || question.question_type === 'mcq_single' || question.question_type === 'mcq_multiple') && (
                                    <div className="space-y-2.5">
                                      {question.question_type === 'mcq_multiple' && (
                                        <p className="text-sm text-gray-600 mb-2 font-medium">(Select all that apply)</p>
                                      )}
                                      {question.options?.map((option, optIndex) => {
                                        const isMultiple = question.question_type === 'mcq_multiple';
                                        const isSelected = isMultiple 
                                          ? (currentAnswers[question.question_id] || []).includes(option.option_id)
                                          : currentAnswers[question.question_id] === option.option_id;
                                        
                                        // Review mode: check if this is the correct answer
                                        const isCorrectAnswer = question.correct_options?.includes(option.option_id);
                                        const showCorrectness = isReviewMode;
                                        
                                        // Determine styling for review mode
                                        let reviewClassName = '';
                                        if (showCorrectness) {
                                          if (isCorrectAnswer && isSelected) {
                                            reviewClassName = 'border-green-600 bg-green-50'; // Correct selection
                                          } else if (isCorrectAnswer && !isSelected) {
                                            reviewClassName = 'border-green-400 bg-green-50'; // Correct answer (not selected)
                                          } else if (!isCorrectAnswer && isSelected) {
                                            reviewClassName = 'border-red-600 bg-red-50'; // Wrong selection
                                          } else {
                                            reviewClassName = 'border-gray-200 bg-gray-50'; // Not selected, not correct
                                          }
                                        }
                                        
                                        return (
                                          <label
                                            key={optIndex}
                                            className={`training-answer-option flex items-start p-3 rounded-lg border-2 transition-all min-w-0 ${
                                              showCorrectness
                                                ? `${reviewClassName} cursor-default` // Review mode styling
                                                : isSelected
                                                  ? 'border-blue-600 bg-blue-50 cursor-pointer'
                                                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                                            }`}
                                          >
                                            <input
                                              type={isMultiple ? "checkbox" : "radio"}
                                              disabled={isReviewMode}
                                              name={`question-${question.question_id}`}
                                              value={option.option_id}
                                              checked={isSelected}
                                              onChange={(e) => handleAnswerChange(question.question_id, option.option_id, isMultiple)}
                                              className="sr-only"
                                            />
                                            <span className={`training-answer-control training-answer-control-faux ${isSelected ? 'is-selected' : ''} w-5 h-5 ${isMultiple ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0 mt-0.5`}>
                                              {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                            </span>
                                            <span className="training-answer-text ml-3 text-base text-gray-900 flex-1 min-w-0 break-words leading-relaxed">{getTranslatedOptionText(option)}</span>
                                            
                                            {/* Review mode indicators - Enhanced */}
                                            {showCorrectness && (
                                              <div className="training-answer-review ml-auto flex items-center gap-2 shrink-0 flex-wrap justify-end">
                                                {isCorrectAnswer && isSelected && (
                                                  <span className="flex items-center px-2.5 py-1 text-xs font-bold bg-green-100 text-green-700 rounded-full border border-green-300">
                                                    ✓ Correct
                                                  </span>
                                                )}
                                                {isCorrectAnswer && !isSelected && (
                                                  <span className="flex items-center px-2.5 py-1 text-xs font-bold bg-green-100 text-green-700 rounded-full border border-green-300">
                                                    ✓ Correct Answer
                                                  </span>
                                                )}
                                                {isSelected && !isCorrectAnswer && (
                                                  <span className="flex items-center px-2.5 py-1 text-xs font-bold bg-red-100 text-red-700 rounded-full border border-red-300">
                                                    ✗ Wrong
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Ranking Type */}
                                  {question.question_type === 'ranking' && (
                                    <div>
                                      <p className="text-sm text-gray-600 mb-2 font-medium">Rank items (1 = highest):</p>
                                      <div className="space-y-2.5">
                                        {question.options?.map((option, optIndex) => (
                                          <div key={optIndex} className="flex items-center space-x-3 p-3 border-2 border-gray-200 rounded-lg">
                                            <input
                                              type="number"
                                              min="1"
                                              max={question.options.length}
                                              placeholder={optIndex + 1}
                                              onChange={(e) => {
                                                const rankings = currentAnswers[question.question_id] || {};
                                                rankings[option.option_id] = parseInt(e.target.value);
                                                handleAnswerChange(question.question_id, rankings);
                                              }}
                                              className="w-16 px-3 py-2 text-base border border-gray-300 rounded"
                                            />
                                            <span className="flex-1 text-base text-gray-900">{getTranslatedOptionText(option)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* True/False */}
                                  {question.question_type === 'true_false' && (
                                    <div className="space-y-2.5">
                                      {['True', 'False'].map((option) => (
                                        (() => {
                                          const isSelected = currentAnswers[question.question_id] === option;
                                          return (
                                        <label
                                          key={option}
                                          className={`training-answer-option flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                            isSelected
                                              ? 'border-blue-600 bg-blue-50'
                                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                          }`}
                                        >
                                          <input
                                            type="radio"
                                            name={`question-${question.question_id}`}
                                            value={option}
                                            checked={isSelected}
                                            onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                                            className="sr-only"
                                          />
                                          <span className={`training-answer-control training-answer-control-faux ${isSelected ? 'is-selected' : ''} w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5`}>
                                            {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                          </span>
                                          <span className="training-answer-text ml-3 text-base text-gray-900">{option}</span>
                                        </label>
                                          );
                                        })()
                                      ))}
                                    </div>
                                  )}
                                </div>
                                
                                {/* Navigation Buttons - Sticky at bottom of card - Hidden when instruction is showing */}
                                {/* Feedback Message Overlay - Bottom Position */}
                                {currentInstructionMessage && !isReviewMode && (
                                  <div className="mx-2.5 my-3 animate-in slide-in-from-bottom duration-300">
                                    <div className={`p-4 rounded-lg border-2 shadow-lg ${
                                      currentInstructionMessage.type === 'correct' 
                                        ? 'bg-green-50 border-green-400' 
                                        : 'bg-red-50 border-red-400'
                                    }`}>
                                      <div className="flex items-start space-x-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                          {currentInstructionMessage.type === 'correct' ? (
                                            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                          ) : (
                                            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                          )}
                                        </div>
                                        <div className="flex-1">
                                          <h4 className={`font-bold text-base mb-1 ${
                                            currentInstructionMessage.type === 'correct' ? 'text-green-800' : 'text-red-800'
                                          }`}>
                                            {currentInstructionMessage.type === 'correct' ? '✓ Correct Answer!' : '✗ Incorrect Answer'}
                                          </h4>
                                          <div 
                                            className={`text-sm instruction-message-content ${
                                              currentInstructionMessage.type === 'correct' ? 'text-green-700' : 'text-red-700'
                                            }`}
                                            dangerouslySetInnerHTML={{ __html: currentInstructionMessage.message }}
                                          />
                                        </div>
                                        <Button
                                          onClick={handleContinueAfterInstruction}
                                          size="sm"
                                          className={`flex-shrink-0 ${
                                            currentInstructionMessage.type === 'correct' 
                                              ? 'bg-green-600 hover:bg-green-700' 
                                              : 'bg-blue-600 hover:bg-blue-700'
                                          } text-white`}
                                        >
                                          {currentQuestionIndex === assessmentQuestions.length - 1 ? 'View Results →' : 'Continue →'}
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {!currentInstructionMessage && (
                                  <div className="training-question-actions flex-shrink-0 px-5 py-3 bg-white border-t border-gray-200 flex items-center justify-between gap-2">
                                    {isReviewMode ? (
                                      // Review mode navigation
                                      <>
                                        <Button
                                          onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                                          disabled={currentQuestionIndex === 0}
                                          variant="outline"
                                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          ← Previous Question
                                        </Button>
                                        
                                        {currentQuestionIndex < assessmentQuestions.length - 1 ? (
                                          <Button
                                            onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white"
                                          >
                                            Next Question →
                                          </Button>
                                        ) : (
                                          <Button
                                            onClick={() => {
                                              setShowAssessment(false);
                                              setIsReviewMode(false);
                                              setReviewAttempt(null);
                                              setCurrentQuestionIndex(0);
                                              setCurrentAnswers({});
                                              toast.success('Review completed');
                                            }}
                                            className="bg-green-600 hover:bg-green-700 text-white px-8"
                                          >
                                            Close Review
                                          </Button>
                                        )}
                                      </>
                                    ) : (
                                      // Normal mode navigation
                                      <>
                                        <Button
                                          onClick={() => {
                                            setCurrentQuestionIndex(currentQuestionIndex - 1);
                                            setCurrentInstructionMessage(null); // Clear instruction when going back
                                          }}
                                          disabled={currentQuestionIndex === 0}
                                          variant="outline"
                                          className="disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          ← Previous
                                        </Button>

                                        <Button
                                          onClick={handleNextQuestion}
                                          disabled={!hasAnswer}
                                          className={`${
                                            currentQuestionIndex === assessmentQuestions.length - 1
                                              ? 'bg-green-600 hover:bg-green-700'
                                              : 'bg-blue-600 hover:bg-blue-700'
                                          } text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
                                        >
                                          {currentQuestionIndex === assessmentQuestions.length - 1 ? 'Submit Assessment →' : 'Next →'}
                                        </Button>
                                        {/* Note: For last question, handleNextQuestion will show feedback first,
                                            then handleContinueAfterInstruction will call handleSubmitAssessment */}
                                      </>
                                    )}
                                  </div>
                                )}
                                
                                {/* Feedback Message Overlay - Bottom Position */}
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : adaptiveMode && activePreview ? (
              <div className="w-full h-full flex flex-col">
                {/* Adaptive Subchapter Header */}
                <div className="bg-gradient-to-r from-purple-900 to-indigo-900 px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <GraduationCap className="w-5 h-5 text-purple-300" />
                    <div>
                      <div className="text-white font-medium text-sm">{activePreview.sectionTitle}</div>
                      <div className="text-purple-300 text-xs">Adaptive Learning • Quick Assessment</div>
                    </div>
                  </div>
                </div>

                <div className="flex-1 bg-gradient-to-br from-gray-50 to-purple-50 overflow-y-auto p-4">
                  {/* Adaptive Instructions Screen */}
                  {showAdaptiveInstructions && !adaptiveSubmitted && !adaptiveWrongAnswer && (
                    <div className="max-w-2xl mx-auto">
                      <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-8">
                        <div className="flex items-center gap-3 mb-5">
                          <div className="bg-purple-100 p-2.5 rounded-xl">
                            <GraduationCap className="w-6 h-6 text-purple-600" />
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">Before You Begin</h3>
                        </div>
                        <p className="text-gray-600 mb-4">Here's how this adaptive learning flow works:</p>
                        <ul className="space-y-3 text-sm text-gray-700 mb-6">
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>You will first be asked a set of questions to assess your understanding of this chapter.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>If answered correctly, you can skip the full module and assessment, and move directly to the <strong>Key Highlights</strong> to save time.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>If not, you will be guided to complete the full learning module for this chapter.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>After completing the module, you will take the module assessment.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>Upon passing the respective chapter, you will return here again where the next chapter will begin with questions.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>Your progress will be saved only after completing all questions; if you exit midway, the chapter will restart from the beginning.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>Please answer each question carefully before submitting.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-purple-500 mt-0.5">•</span>
                            <span>Ensure a stable internet connection for a smooth experience.</span>
                          </li>
                        </ul>
                        <div className="flex justify-center">
                          <Button
                            onClick={() => setShowAdaptiveInstructions(false)}
                            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-2.5 text-base"
                          >
                            Begin →
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Adaptive Questions - Not yet submitted */}
                  {!adaptiveSubmitted && !adaptiveWrongAnswer && !showAdaptiveInstructions && adaptiveSectionQuestions.length > 0 && (
                    <div className="max-w-2xl mx-auto">
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-bold text-gray-900">Quick Knowledge Check</h3>
                          <span className="text-sm text-blue-600 font-medium">
                            {adaptiveCurrentIndex + 1} / {adaptiveSectionQuestions.length}
                          </span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2">
                          <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${((adaptiveCurrentIndex + 1) / adaptiveSectionQuestions.length) * 100}%` }} />
                        </div>
                      </div>

                      {(() => {
                        const q = adaptiveSectionQuestions[adaptiveCurrentIndex];
                        const answer = adaptiveAnswers[q.question_id];
                        const isMultiple = q.question_type === 'mcq_multiple';
                        return (
                          <div key={q.question_id} className="training-question-card relative bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                            {/* Question Content */}
                            <div className="training-question-content p-5 pb-2">
                              <div className="flex items-start gap-2 mb-3 min-w-0">
                                <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-xs">
                                  {adaptiveCurrentIndex + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <h4 className="training-question-text text-lg font-semibold text-gray-900 mb-2 break-words">{getTranslatedQuestionText(q)}</h4>
                                  {q.question_image && (
                                    <img
                                      src={q.question_image}
                                      alt="Question"
                                      className="w-full max-w-md rounded-lg mb-3 shadow-sm"
                                    />
                                  )}
                                </div>
                              </div>

                              {['mcq_single', 'mcq_multiple'].includes(q.question_type) && (
                                <div className="space-y-2.5">
                                  {isMultiple && (
                                    <p className="text-sm text-gray-600 mb-2 font-medium">(Select all that apply)</p>
                                  )}
                                  {(q.options || []).map((opt, optIndex) => {
                                    const isSelected = isMultiple
                                      ? (answer || []).includes(opt.option_id)
                                      : answer === opt.option_id;
                                    return (
                                      <label
                                        key={opt.option_id || optIndex}
                                        className={`training-answer-option flex items-start p-3 rounded-lg border-2 transition-all min-w-0 ${
                                          isSelected
                                            ? 'border-blue-600 bg-blue-50 cursor-pointer'
                                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50 cursor-pointer'
                                        }`}
                                        onClick={() => { if (!adaptiveInstructionMsg) handleAdaptiveAnswer(q.question_id, opt.option_id, isMultiple); }}
                                      >
                                        <input
                                          type={isMultiple ? 'checkbox' : 'radio'}
                                          name={`adaptive-question-${q.question_id}`}
                                          checked={isSelected}
                                          readOnly
                                          className="sr-only"
                                        />
                                        <span className={`training-answer-control training-answer-control-faux ${isSelected ? 'is-selected' : ''} w-5 h-5 ${isMultiple ? 'rounded' : 'rounded-full'} border-2 flex items-center justify-center shrink-0 mt-0.5`}>
                                          {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                                        </span>
                                        <span className="training-answer-text ml-3 text-base text-gray-900 flex-1 min-w-0 break-words leading-relaxed">{getTranslatedOptionText(opt)}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Adaptive Feedback Overlay - matches conventional instruction overlay */}
                            {adaptiveInstructionMsg && (
                              <div className="mx-2.5 my-3 animate-in slide-in-from-bottom duration-300">
                                <div className={`p-4 rounded-lg border-2 shadow-lg ${
                                  adaptiveInstructionMsg.type === 'correct'
                                    ? 'bg-green-50 border-green-400'
                                    : 'bg-red-50 border-red-400'
                                }`}>
                                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                                    <div className="flex-shrink-0 mt-0.5">
                                      {adaptiveInstructionMsg.type === 'correct' ? (
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      ) : (
                                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <h4 className={`font-bold text-base mb-1 ${
                                        adaptiveInstructionMsg.type === 'correct' ? 'text-green-800' : 'text-red-800'
                                      }`}>
                                        {adaptiveInstructionMsg.type === 'correct' ? '✓ Correct Answer!' : '✗ Incorrect Answer'}
                                      </h4>
                                      <div
                                        className={`text-sm instruction-message-content ${
                                          adaptiveInstructionMsg.type === 'correct' ? 'text-green-700' : 'text-red-700'
                                        }`}
                                        dangerouslySetInnerHTML={{ __html: adaptiveInstructionMsg.message }}
                                      />
                                    </div>
                                    <Button
                                      onClick={handleAdaptiveContinueAfterInstruction}
                                      size="sm"
                                      className={`flex-shrink-0 ${
                                        adaptiveInstructionMsg.type === 'correct'
                                          ? 'bg-green-600 hover:bg-green-700'
                                          : 'bg-blue-600 hover:bg-blue-700'
                                      } text-white`}
                                    >
                                      {adaptiveCurrentIndex >= adaptiveSectionQuestions.length - 1 ? 'View Results →' : 'Continue →'}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {!adaptiveInstructionMsg && (
                              <div className="training-question-actions flex-shrink-0 px-5 py-3 bg-white border-t border-gray-200 flex items-center justify-between gap-2">
                                <Button
                                  variant="outline"
                                  disabled={adaptiveCurrentIndex === 0}
                                  onClick={() => { setAdaptiveCurrentIndex(i => i - 1); setAdaptiveInstructionMsg(null); }}
                                  className="disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  ← Previous
                                </Button>
                                <Button
                                  onClick={handleAdaptiveNext}
                                  disabled={!answer || (Array.isArray(answer) && answer.length === 0)}
                                  className={`${
                                    adaptiveCurrentIndex === adaptiveSectionQuestions.length - 1
                                      ? 'bg-green-600 hover:bg-green-700'
                                      : 'bg-blue-600 hover:bg-blue-700'
                                  } text-white disabled:bg-gray-400 disabled:cursor-not-allowed`}
                                >
                                  {adaptiveCurrentIndex === adaptiveSectionQuestions.length - 1 ? 'Submit Assessment →' : 'Next →'}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Adaptive Wrong Answer — Encouraging Redirect */}
                  {!adaptiveSubmitted && adaptiveWrongAnswer && (
                    <div className="max-w-2xl mx-auto text-center">
                      <div className="bg-white rounded-2xl shadow-lg border border-amber-200 p-8">
                        <div className="text-6xl mb-4">📖</div>
                        <h2 className="text-2xl font-bold text-amber-700 mb-4">Oops! That wasn't the correct answer, but that's okay.</h2>
                        <p className="text-gray-600 mb-2">Learning is a journey, and mistakes are part of the process.</p>
                        <p className="text-gray-600 mb-2">Let's quickly revisit this chapter to strengthen your understanding.</p>
                        <p className="text-gray-500 text-sm mb-8">We'll see you back here, stronger and more confident!</p>
                        
                        <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-2" onClick={handleGoToTraining}>
                          <Play className="w-4 h-4 mr-2" /> Proceed to Chapter
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Adaptive Results - Passed */}
                  {adaptiveSubmitted && adaptiveScore >= 80 && !showAdaptiveReview && (() => {
                    const filteredSections = getFilteredSections();
                    const allSectionsCompleted = filteredSections.length > 0 && 
                      filteredSections.every(s => isSectionViewed(s) || isSectionAdaptivelyCompleted(s));
                    const isLastSection = currentSectionIndex === filteredSections.length - 1;

                    // Show Chapter Completed only on revisit (all sections already done before this session).
                    // During live completion of the final subchapter, show the Great Job screen first
                    // so the user can view Key Highlights and review answers before seeing chapter summary.
                    // If the user failed adaptive earlier and the chapter assessment is still pending,
                    // do NOT show adaptive question stats as chapter results — show pending state instead.
                    if (allSectionsCompleted && adaptiveRevisit && isChapterAssessmentPending()) {
                      return (
                        <div className="w-full px-4 py-2 flex items-center justify-center h-full">
                          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-200 w-full max-w-[90%]">
                            <div className="text-4xl mb-2">📋</div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">All subchapters completed</h3>
                            <p className="text-amber-700 text-base font-medium mb-4">Chapter Assessment pending</p>
                            <p className="text-sm text-gray-600 mb-4">
                              You need to complete the Chapter Assessment before this chapter is marked complete.
                            </p>
                            <Button
                              onClick={async () => {
                                setAdaptiveMode(false);
                                setAdaptiveSubmitted(false);
                                await fetchPreAssessmentNotes();
                                setShowPreAssessmentNotes(true);
                              }}
                              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white px-6"
                            >
                              <ClipboardCheck className="w-4 h-4 mr-2" />
                              Go to Chapter Assessment
                            </Button>
                          </div>
                        </div>
                      );
                    }
                    if (allSectionsCompleted && adaptiveRevisit) {
                      // === FULL CHAPTER COMPLETED SCREEN (revisit only) ===
                      return (
                        <div className="w-full px-4 py-2 flex items-center justify-center h-full">
                          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-200 w-full max-w-[90%]">
                            <div className="text-4xl mb-2">✅</div>
                            <h3 className="text-lg font-bold text-gray-900 mb-0.5">Chapter Completed</h3>
                            <p className="text-green-700 text-base font-semibold mb-3">{content?.title || 'This chapter'}</p>

                            {/* Result Statistics */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-blue-600">{adaptiveSectionQuestions.length}</span>
                                <span className="text-xs text-blue-600 block mt-1">Questions</span>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-green-600">{adaptiveCorrectCount}</span>
                                <span className="text-xs text-green-600 block mt-1">Correct</span>
                              </div>
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-red-500">{Math.max(0, adaptiveSectionQuestions.length - adaptiveCorrectCount)}</span>
                                <span className="text-xs text-red-500 block mt-1">Wrong</span>
                              </div>
                            </div>

                            {/* Assessment Passed Banner */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-2 mb-3">
                              <div className="flex items-center justify-center">
                                <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                                <span className="text-sm text-green-700 font-medium">
                                  Assessment Passed — Score: {adaptiveScore}%
                                </span>
                              </div>
                            </div>

                            {/* Total Time Saved */}
                            {(() => {
                              const totalTimeSaved = (content?.sections || []).reduce((total, sec, idx) => {
                                const matchingSection = filteredSections.find(fs => fs.sectionIndex === idx);
                                if (matchingSection && adaptiveCompletedSections.has(matchingSection.globalIndex) && sec.content_duration) {
                                  return total + sec.content_duration;
                                }
                                return total;
                              }, 0);
                              const displayTime = totalTimeSaved > 0 ? totalTimeSaved : (currentSectionMeta?.content_duration || 0);
                              return displayTime > 0 ? (
                                <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 mb-3">
                                  <div className="flex items-center justify-center">
                                    <span className="text-sm text-purple-700 font-medium">
                                      🕐 You saved a total of {displayTime} {displayTime === 1 ? 'minute' : 'minutes'} across this chapter.
                                    </span>
                                  </div>
                                </div>
                              ) : null;
                            })()}

                            <div className="flex flex-col items-center space-y-2">
                              <button
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1.5 transition-colors"
                                onClick={() => setShowAdaptiveReview(true)}
                              >
                                <ClipboardCheck className="w-4 h-4" /> Review Your Answers
                              </button>

                              <Button
                                onClick={() => handleModalClose(false)}
                                className="bg-green-600 hover:bg-green-700 text-white px-6"
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Go back to Training Dashboard
                              </Button>

                              {topicFeedback && !topicFeedback.already_submitted && (
                                <Button
                                  onClick={async () => {
                                    const topicIdForFeedback = getTopicIdForFeedback();
                                    if (!topicIdForFeedback) return;
                                    try {
                                      const token = localStorage.getItem('token');
                                      const feedbackResponse = await axios.get(`${API}/feedback/topic/${topicIdForFeedback}`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      if (feedbackResponse.data.has_feedback && !feedbackResponse.data.already_submitted) {
                                        const feedbackSet = feedbackResponse.data.feedback_set || {
                                          feedback_set_id: `topic_${topicIdForFeedback}`,
                                          title: 'Training Feedback',
                                          description: 'Please share your feedback on this training',
                                          questions: feedbackResponse.data.questions || []
                                        };
                                        onClose({
                                          topicId: topicIdForFeedback,
                                          topicName: content?.title,
                                          feedbackSet,
                                          isMandatory: true
                                        });
                                      }
                                    } catch (e) { /* ignore */ }
                                  }}
                                  variant="outline"
                                  className="w-full max-w-md"
                                >
                                  Share Your Feedback
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      // === SUBCHAPTER COMPLETED SCREEN ===
                      return (
                        <div className="w-full px-4 py-2 flex items-center justify-center h-full">
                          <div className="bg-white rounded-xl shadow-lg p-6 text-center border border-gray-200 w-full max-w-[90%]">
                            <div className="text-4xl mb-2">🎉</div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Great job!</h3>
                            <p className="text-gray-600 mb-0.5 text-sm">You have completed this subchapter.</p>
                            <p className="text-purple-700 font-semibold text-sm mb-3">{activePreview?.sectionTitle || 'Subchapter'}</p>

                            {/* Time saved for this subchapter */}
                            {currentSectionMeta?.content_duration > 0 && (
                              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 mb-3">
                                <div className="flex items-center justify-center">
                                  <span className="text-sm text-purple-700 font-medium">
                                    🕐 You saved {currentSectionMeta.content_duration} {currentSectionMeta.content_duration === 1 ? 'minute' : 'minutes'} by completing this efficiently.
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Score for this subchapter */}
                            <div className="grid grid-cols-3 gap-3 mb-3">
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-blue-600">{adaptiveSectionQuestions.length}</span>
                                <span className="text-xs text-blue-600 block mt-1">Questions</span>
                              </div>
                              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-green-600">{adaptiveCorrectCount}</span>
                                <span className="text-xs text-green-600 block mt-1">Correct</span>
                              </div>
                              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <span className="text-2xl font-bold text-red-500">{Math.max(0, adaptiveSectionQuestions.length - adaptiveCorrectCount)}</span>
                                <span className="text-xs text-red-500 block mt-1">Wrong</span>
                              </div>
                            </div>

                            <div className="flex flex-col items-center space-y-2">
                              <button
                                className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1.5 transition-colors"
                                onClick={() => setShowAdaptiveReview(true)}
                              >
                                <ClipboardCheck className="w-4 h-4" /> Review Your Answers
                              </button>

                              {/* View Key Highlights */}
                              <Button
                                onClick={() => {
                                  if (currentSectionMeta?.summary_video || currentSectionMeta?.summary_pdf) {
                                    handleViewSummary();
                                  } else {
                                    toast.info('Key Highlights not available. Please try again later or contact the admin.');
                                  }
                                }}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                              >
                                <BookOpen className="w-4 h-4 mr-2" />
                                View Key Highlights
                              </Button>

                              <p className="text-xs text-gray-500">
                                Want to dive deeper?{' '}
                                <button
                                  type="button"
                                  onClick={handleWatchFullModule}
                                  className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                                >
                                  Explore the full chapter here.
                                </button>
                              </p>

                              {/* Go to Next Subchapter - only if not last */}
                              {!isLastSection && (
                                <Button
                                  onClick={async () => {
                                    // Mark current section as viewed before moving to next
                                    const sectionIndex = activePreview?.sectionIndex;
                                    if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                                      await markSectionViewed(sectionIndex);
                                    }
                                    goToNextSection(true);
                                  }}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                                >
                                  Go to Next Subchapter →
                                </Button>
                              )}

                              {/* Last subchapter CTA — depends on the ENTIRE chapter journey:
                                  - Scenario A (no fallback anywhere): chapter is 100% complete → Go Back to Dashboard
                                  - Scenario B (fallback on any subchapter): chapter assessment required → Go to Chapter Assessment */}
                              {isLastSection && !isChapterAssessmentPending() && (
                                <Button
                                  onClick={() => handleModalClose(false)}
                                  className="bg-green-600 hover:bg-green-700 text-white px-6"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Go Back to Training Dashboard
                                </Button>
                              )}
                              {isLastSection && isChapterAssessmentPending() && (
                                <Button
                                  onClick={async () => {
                                    const sectionIndex = activePreview?.sectionIndex;
                                    if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                                      await markSectionViewed(sectionIndex);
                                    }
                                    await fetchPreAssessmentNotes();
                                    setAdaptiveMode(false);
                                    setAdaptiveSubmitted(false);
                                    setShowPreAssessmentNotes(true);
                                  }}
                                  className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white px-6"
                                >
                                  <ClipboardCheck className="w-4 h-4 mr-2" />
                                  Go to Chapter Assessment
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    }
                  })()}

                  {/* Adaptive Answer Review */}
                  {adaptiveSubmitted && adaptiveScore >= 80 && showAdaptiveReview && (
                    <div className="max-w-2xl mx-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                          <ClipboardCheck className="w-5 h-5 text-blue-600" /> Answer Review
                        </h3>
                        <button
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                          onClick={() => setShowAdaptiveReview(false)}
                        >
                          ← Back to Results
                        </button>
                      </div>
                      {/* Load all adaptive questions across subchapters */}
                      <AdaptiveFullReview
                        content={content}
                        adaptiveCompletedSections={adaptiveCompletedSections}
                        getFilteredSections={getFilteredSections}
                        adaptiveSectionQuestions={adaptiveSectionQuestions}
                        adaptiveAnswers={adaptiveAnswers}
                        adaptiveRevisit={adaptiveRevisit}
                        selectedLanguage={selectedLanguage}
                        getTranslatedQuestionText={getTranslatedQuestionText}
                        getTranslatedOptionText={getTranslatedOptionText}
                        API={API}
                      />
                    </div>
                  )}

                  {/* Adaptive Results - Failed */}
                  {adaptiveSubmitted && adaptiveScore < 80 && (
                    <div className="max-w-2xl mx-auto px-6 py-8">
                      <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-200">
                        <h3 className="text-2xl font-bold text-red-600 mb-4">Assessment Not Passed</h3>
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mb-6">
                          <p className="text-lg font-semibold text-gray-800 leading-relaxed">
                            Sorry! You have scored <span className="text-red-600 font-bold">{adaptiveScore}%</span>, while the minimum passing score is <span className="text-red-600 font-bold">80%</span>.
                          </p>
                          <p className="text-base text-gray-700 mt-3">
                            We recommend going through the training to build better understanding.
                          </p>
                        </div>

                        {/* Result Statistics — same 3-column grid */}
                        <div className="grid grid-cols-3 gap-3 mb-6">
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <span className="text-2xl font-bold text-blue-600">{adaptiveSectionQuestions.length}</span>
                            <span className="text-xs text-blue-600 block mt-1">Questions</span>
                          </div>
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <span className="text-2xl font-bold text-green-600">{adaptiveCorrectCount}</span>
                            <span className="text-xs text-green-600 block mt-1">Correct</span>
                          </div>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <span className="text-2xl font-bold text-red-500">{Math.max(0, adaptiveSectionQuestions.length - adaptiveCorrectCount)}</span>
                            <span className="text-xs text-red-500 block mt-1">Wrong</span>
                          </div>
                        </div>

                        <div className="flex flex-col items-center space-y-3">
                          <Button
                            onClick={handleGoToTraining}
                            className="w-full max-w-md bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                          >
                            <Play className="w-4 h-4 mr-2" /> Start Training Content
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : activePreview ? (
              <div className="w-full h-full flex flex-col">
                {/* Preview Header */}
                <div className="bg-gray-900 px-4 py-2 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {showSummaryView ? <BookOpen className="w-5 h-5 text-purple-300" /> : getContentIcon(activePreview.content_type)}
                    <div>
                      <div className="text-white font-medium text-sm">{activePreview.sectionTitle}{showSummaryView ? ' — Key Highlights' : ''}</div>
                      <div className="text-gray-400 text-xs capitalize flex items-center space-x-2">
                        <span>{showSummaryView ? 'Welcome to the Key Highlights of this chapter' : activePreview.content_type}</span>
                        <span className="text-gray-600">•</span>
                        <span>Chapter {currentSectionIndex + 1} of {getFilteredSections().length}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isStaffOrManager(user?.role) && viewedSections.has(activePreview.sectionIndex) && (
                      <Badge className="bg-green-500 text-white">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Completed
                      </Badge>
                    )}
                    {completionScreenDismissed && currentStatus === 'completed' && (
                      <Button
                        onClick={() => {
                          setShowCompletionScreen(true);
                          setCompletionScreenDismissed(false);
                        }}
                        size="sm"
                        variant="outline"
                        className="bg-white/10 hover:bg-white/20 text-white border-white/30 text-xs"
                      >
                        ← Back to Chapter Summary
                      </Button>
                    )}
                    {viewingFullModuleFromAdaptive && !showSummaryView && (
                      <Button
                        onClick={handleBackToAdaptiveSummary}
                        size="sm"
                        variant="outline"
                        className="bg-white/10 hover:bg-white/20 text-white border-white/30 text-xs"
                      >
                        ← Back to Summary
                      </Button>
                    )}
                  </div>
                </div>

                {/* Key Highlights Content (Summary View) */}
                {showSummaryView ? (
                  <>
                  {showKeyHighlightsCompletion ? (
                    <div className="flex-1 bg-gradient-to-br from-gray-50 to-green-50 overflow-y-auto p-4">
                      <div className="max-w-2xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-lg border border-green-200 p-8 text-center">
                          <div className="text-5xl mb-4">🎉</div>
                          <h3 className="text-2xl font-bold text-green-700 mb-3">Topic Completed!</h3>
                          <p className="text-gray-600 mb-6 text-base leading-relaxed">
                            You've successfully completed all chapters of this chapter through adaptive learning.
                          </p>

                          {/* Score */}
                          <div className="flex items-center justify-center gap-4 mb-6">
                            <div className="bg-green-100 rounded-lg px-5 py-3">
                              <span className="text-3xl font-bold text-green-600">100%</span>
                              <span className="text-xs text-green-600 block mt-1">Final Score</span>
                            </div>
                          </div>

                          {/* Time Saved */}
                          {(() => {
                            const totalTimeSaved = (content?.sections || []).reduce((total, section) => {
                              return total + (section.content_duration || 0);
                            }, 0);
                            if (totalTimeSaved > 0) {
                              return (
                                <div className="bg-purple-50 rounded-lg px-5 py-3 mb-6 inline-block">
                                  <p className="text-purple-700 font-semibold text-sm">
                                    🕐 You saved a total of {totalTimeSaved} {totalTimeSaved === 1 ? 'minute' : 'minutes'} by completing this chapter efficiently.
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          <Button
                            onClick={() => handleModalClose(false)}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 text-base"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Go back to Training Dashboard
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                    {/* Key Highlights player — same layout as main content player */}
                    <div className="flex-1 bg-black flex items-center justify-center overflow-hidden">
                      {currentSectionMeta?.summary_video && (
                        <SummaryPlayer type="video" s3Key={currentSectionMeta.summary_video.file_url} fileName={currentSectionMeta.summary_video.file_name} onVideoEnded={currentSectionIndex >= getFilteredSections().length - 1 ? handleLastSectionFinish : undefined} />
                      )}
                      {currentSectionMeta?.summary_pdf && (
                        <SummaryPlayer type="pdf" s3Key={currentSectionMeta.summary_pdf.file_url} fileName={currentSectionMeta.summary_pdf.file_name} />
                      )}
                      {!currentSectionMeta?.summary_video && !currentSectionMeta?.summary_pdf && (
                        <p className="text-sm text-gray-400">No Key Highlights content available for this chapter.</p>
                      )}
                    </div>
                    {/* Footer bar — matching main content player footer */}
                    <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                      {currentSectionIndex < getFilteredSections().length - 1 ? (
                            <Button
                              onClick={async () => {
                                // Mark current section as viewed before moving to next
                                const sectionIndex = activePreview?.sectionIndex;
                                if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                                  await markSectionViewed(sectionIndex);
                                }
                                setShowSummaryView(false);
                                // Skip lock check since we just marked the section as viewed
                                // (React state update may not have propagated yet)
                                goToNextSection(true);
                              }}
                              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7"
                            >
                              Go to Next Subchapter →
                            </Button>
                          ) : (
                            <Button
                              onClick={handleLastSectionFinish}
                              className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-7"
                            >
                              Finish ✓
                            </Button>
                          )}
                    </div>
                    </>
                  )}
                  </>
                ) : (
                <>
                {/* Preview Content */}
                <div className="flex-1 bg-black flex items-center justify-center overflow-hidden relative">
                  {/* Fallback CTA — appears ONLY after the video has fully ended (backup if the
                      automatic transition to the post-assessment does not occur). */}
                  {isSubchapterWise() && videoEnded && !scwPassedRef.current.has(activePreview?.sectionIndex) && (
                    <button
                      onClick={async () => {
                        const si = activePreview?.sectionIndex;
                        if (si !== undefined) { await markSectionViewed(si); }
                      }}
                      className="absolute bottom-3 right-3 z-20 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-lg"
                    >
                      Continue to Assessment →
                    </button>
                  )}
                  {activePreview.content_type === 'video' && (
                    <>
                      {/* Check if video URL exists */}
                      {!(activePreview.file_url || activePreview.file_name) ? (
                        <div className="text-white text-center p-8">
                          <p className="text-lg mb-2">Video not available for {selectedLanguage}</p>
                          <p className="text-gray-400 text-sm">The content file for this language has not been uploaded yet.</p>
                        </div>
                      ) : isEmbeddedVideo(activePreview.file_url || activePreview.file_name) ? (
                        <iframe
                          src={getEmbedUrl(activePreview.file_url || activePreview.file_name)}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          key={activePreview.file_url || activePreview.file_name} // Force re-render when source changes
                          ref={videoRef}
                          controls
                          autoPlay
                          className="w-full h-full"
                          onTimeUpdate={handleVideoProgress}
                          onEnded={handleVideoEnded}
                          onError={(e) => {
                            console.error('[Video] Load error:', e, 'URL:', activePreview.file_url || activePreview.file_name);
                            toast.error('Unable to load this media. Please verify S3 configuration or contact the administrator.');
                          }}
                        >
                          <source src={activePreview.file_url || activePreview.file_name} type="video/mp4" />
                          <source src={activePreview.file_url || activePreview.file_name} type="video/webm" />
                          <source src={activePreview.file_url || activePreview.file_name} type="video/ogg" />
                        </video>
                      )}
                    </>
                  )}
                  
                  {activePreview.content_type === 'pdf' && (
                    <>
                      {!(activePreview.file_url || activePreview.file_name) ? (
                        <div className="text-white text-center p-8">
                          <p className="text-lg mb-2">PDF not available for {selectedLanguage}</p>
                          <p className="text-gray-400 text-sm">The content file for this language has not been uploaded yet.</p>
                        </div>
                      ) : (
                        <iframe
                          src={activePreview.file_url || activePreview.file_name}
                          className="w-full h-full"
                          title={`${activePreview.language} PDF`}
                        />
                      )}
                    </>
                  )}
                  
                  {activePreview.content_type === 'image' && (
                    <>
                      {!(activePreview.file_url || activePreview.file_name) ? (
                        <div className="text-white text-center p-8">
                          <p className="text-lg mb-2">Image not available for {selectedLanguage}</p>
                          <p className="text-gray-400 text-sm">The content file for this language has not been uploaded yet.</p>
                        </div>
                      ) : (
                        <img
                          src={activePreview.file_url || activePreview.file_name}
                          alt={activePreview.language}
                          className="max-w-full max-h-full object-contain"
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Content Action Bar — Assessment or Next Subchapter */}
                {isStaffOrManager(user?.role) && (() => {
                  const sectionViewed = viewedSections.has(activePreview?.sectionIndex);
                  const isNonVideo = activePreview?.content_type !== 'video';
                  const filteredSections = getFilteredSections();
                  const isLastSection = currentSectionIndex === filteredSections.length - 1;
                  const canShow = isNonVideo || sectionViewed;

                  // Sub-chapter-wise: no chapter assessment. Once the content is viewable, offer a single
                  // action that launches this sub-chapter's POST assessment (markSectionViewed defers to it).
                  if (isSubchapterWise()) {
                    if (!canShow) return null;
                    return (
                      <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                        <Button
                          onClick={async () => {
                            const sectionIndex = activePreview?.sectionIndex;
                            if (sectionIndex !== undefined) { await markSectionViewed(sectionIndex); }
                          }}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7"
                        >
                          Continue to Post-Assessment →
                        </Button>
                      </div>
                    );
                  }

                  const hasAssessment = !!topicAssessment;
                  // In adaptive mode we skip the final assessment — unless the user failed
                  // an earlier subchapter and fell back to full content (chapter assessment required)
                  const isAdaptiveActionBar = getEffectiveTrainingMode() === 'adaptive' && !adaptiveFailedSession;
                  const allSectionsViewed = filteredSections.length > 0 &&
                    filteredSections.every(s => isSectionViewed(s));

                  // PRIORITY 1: Last section with assessment → show "Go to Assessment" (conventional only)
                  // In adaptive mode, skip assessment — chapter completes from subchapters.
                  // Only show when ALL sections are completed (current section may still need viewing)
                  if (isLastSection && hasAssessment && canShow && !isAdaptiveActionBar) {
                    return (
                      <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                        <Button
                          onClick={async () => {
                            const sectionIndex = activePreview?.sectionIndex;
                            if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                              await markSectionViewed(sectionIndex);
                            }
                            await handleTakeAssessmentClick();
                          }}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-7"
                        >
                          Go to Assessment →
                        </Button>
                      </div>
                    );
                  }

                  // For last section with video not yet viewed, and assessment present: show nothing
                  // (handleVideoEnded will auto-navigate to assessment) — conventional only
                  if (isLastSection && hasAssessment && !canShow && !isAdaptiveActionBar) {
                    return null;
                  }

                  // Non-last section: show "Next Subchapter"
                  if (!isLastSection && canShow) {
                    return (
                      <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                        <Button
                          onClick={async () => {
                            const sectionIndex = activePreview?.sectionIndex;
                            if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                              await markSectionViewed(sectionIndex);
                            }
                            goToNextSection(true);
                          }}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7"
                        >
                          Next Subchapter →
                        </Button>
                      </div>
                    );
                  }

                  // Last section, assessment passed or no assessment
                  if (isLastSection && canShow) {
                    // Review mode: topic already completed — show Back to Chapter Summary
                    if (completionScreenDismissed && (currentStatus === 'completed' || backendLoadedStatus === 'completed')) {
                      return (
                        <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                          <Button
                            onClick={() => {
                              setShowCompletionScreen(true);
                              setCompletionScreenDismissed(false);
                            }}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 h-7"
                          >
                            ← Back to Chapter Summary
                          </Button>
                        </div>
                      );
                    }
                    // Don't show Mark Complete until we know for sure there's no assessment
                    if (!assessmentFetchDone) {
                      return (
                        <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                          <span className="text-gray-400 text-xs">Loading...</span>
                        </div>
                      );
                    }
                    // Mark Complete (only when no unpassed assessment exists)
                    return (
                      <div className="bg-gray-900 px-4 py-2 flex items-center justify-end">
                        <Button
                          onClick={async () => {
                            const sectionIndex = activePreview?.sectionIndex;
                            if (sectionIndex !== undefined && !viewedSections.has(sectionIndex)) {
                              await markSectionViewed(sectionIndex);
                            }
                            handleLastSectionFinish();
                          }}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7"
                        >
                          Finish ✓
                        </Button>
                      </div>
                    );
                  }

                  return null;
                })()}
                </>)}
              </div>
            ) : isHybridAssessmentMode ? (
              /* Hybrid Chapter Info Panel - Shows instead of "Select a subchapter" */
              <div className="flex items-center justify-center h-full p-8">
                <div className="max-w-md w-full">
                  {/* Hybrid Badge */}
                  <div className="flex justify-center mb-6">
                    <Badge className="bg-purple-500 text-white text-sm px-4 py-2 rounded-full font-medium">
                      🎯 Hybrid Training
                    </Badge>
                  </div>
                  
                  {/* Chapter Name */}
                  <h2 className="text-2xl font-bold text-white text-center mb-6">
                    {hybridTopicName || content?.title || 'Hybrid Chapter'}
                  </h2>
                  
                  {/* Info Cards */}
                  <div className="space-y-4">
                    {/* Attendance Confirmed */}
                    <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500/30 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-300">Attendance Confirmed</p>
                          <p className="text-sm text-green-400/80">Your presence has been recorded</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Info */}
                    <div className="bg-purple-500/20 border border-purple-400/30 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-purple-300">Current Progress</span>
                        <span className="text-lg font-bold text-purple-300">80%</span>
                      </div>
                      <div className="w-full bg-purple-900/50 rounded-full h-2.5">
                        <div className="bg-purple-500 h-2.5 rounded-full" style={{ width: '80%' }}></div>
                      </div>
                      <p className="text-xs text-purple-400/80 mt-2">Pass the assessment for 100% completion</p>
                    </div>
                    
                    {/* Assessment Info */}
                    <div className="bg-blue-500/20 border border-blue-400/30 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-500/30 rounded-full flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-blue-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-blue-300">Assessment Ready</p>
                          <p className="text-sm text-blue-400/80">Complete the assessment to finish this chapter</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Instructions */}
                    <div className="bg-gray-800/50 border border-gray-600/30 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <ClipboardCheck className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-300">Click <strong>"Start Assessment"</strong> on the right to begin your assessment.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-center text-gray-400">
                <div>
                  <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Select a chapter to start learning</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Progress on mobile, full sections panel on desktop */}
          <div className="training-learning-side w-full md:w-[420px] bg-white border-t md:border-t-0 md:border-l border-gray-200 flex flex-col overflow-hidden flex-1 md:flex-initial min-w-0">

            {/* Sections List - mobile turns this into a compact progress card plus sheet */}
            <div className="training-sections-panel flex-1 overflow-y-auto px-4 pb-4 pt-2 space-y-2 min-h-0">
                {isStaffOrManager(user?.role) && (
                  <div className="training-progress-card bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-900">Your Progress</span>
                      <button
                        type="button"
                        onClick={() => setShowMobileSubchapters(true)}
                        className="training-subchapters-trigger hidden w-9 h-9 items-center justify-center rounded-full bg-white border border-blue-200 text-blue-700 shadow-sm active:scale-95 transition-transform"
                        aria-label="Open subchapters"
                        title="Subchapters"
                      >
                        <ListChecks className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="text-xs text-blue-700 mb-2">
                      {(() => {
                        const filtered = getFilteredSections();
                        return `${filtered.filter(s => isSectionViewed(s)).length} of ${filtered.length} subchapters completed`;
                      })()}
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${currentProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Display sections filtered by selected language */}
                <div className={`training-subchapters-panel space-y-2 ${showMobileSubchapters ? 'is-open' : ''}`}>
                  <div className="training-subchapters-backdrop" onClick={() => setShowMobileSubchapters(false)} />
                  <div className="training-subchapters-sheet">
                  <div className="training-subchapters-sheet-header hidden items-center justify-between border-b border-gray-200 px-4 py-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {content.content_structure === 'standalone' ? 'Content' : 'Subchapters'}
                      </h3>
                      <p className="text-xs text-gray-500">{selectedLanguage}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowMobileSubchapters(false)}
                      className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center"
                      aria-label="Close subchapters"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="training-subchapters-list space-y-2">
                  <div className="training-subchapters-desktop-heading flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {content.content_structure === 'standalone' ? 'Content' : 'Subchapters'}
                    </h3>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {selectedLanguage}
                    </span>
                  </div>
                  
                  {(() => {
                    const filteredSections = getFilteredSections();
                    return filteredSections.map((section, index) => {
                      const isViewed = isSectionViewed(section);
                      const isActive = activePreview?.sectionIndex === section.globalIndex;
                      const isCurrent = currentSectionIndex === index;
                      const isLocked = isStaffOrManager(user?.role) && isSectionLocked(index, filteredSections);
                    
                      return (
                        <button
                          key={index}
                          onClick={() => {
                            handleSectionClick(section, index, filteredSections);
                            setShowMobileSubchapters(false);
                          }}
                          className={`w-full text-left rounded-lg p-3 border-2 transition-all ${
                            isLocked
                              ? 'border-gray-200 bg-gray-100 opacity-55 cursor-not-allowed'
                              : completionScreenDismissed && (isActive || isCurrent)
                              ? 'border-blue-500 bg-blue-50'
                              : currentStatus === 'completed' && isViewed
                              ? 'border-green-500 bg-green-50'
                              : isActive || isCurrent
                              ? 'border-blue-500 bg-blue-50'
                              : isViewed
                              ? 'border-green-500 bg-green-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              {!section.isStandalone && (
                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                  isLocked ? 'bg-gray-400 text-white' : currentStatus === 'completed' && isViewed ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                                }`}>
                                  {index + 1}
                                </span>
                              )}
                              {isLocked && <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                              {!isLocked && isViewed && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                              {!isLocked && getContentIcon(section.langVersion.content_type)}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <div className={`font-medium text-sm truncate flex-1 ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>{section.sectionTitle}</div>
                                  {isCurrent && !isLocked && !isViewed && currentStatus !== 'completed' && (
                                    <Badge className="bg-blue-600 text-white text-xs flex-shrink-0">Playing</Badge>
                                  )}
                                  {completionScreenDismissed && (isActive || isCurrent) && !isLocked && (
                                    <Badge className="bg-blue-600 text-white text-xs flex-shrink-0">Playing</Badge>
                                  )}
                                </div>
                                <div className={`text-xs capitalize ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>{section.langVersion.content_type}</div>
                              </div>
                            </div>
                            {isLocked 
                              ? <Lock className="w-4 h-4 text-gray-300 flex-shrink-0" />
                              : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            }
                          </div>
                        </button>
                      );
                    });
                  })()}
                  
                  {/* Assessment segment — shown for conventional chapters, and adaptive chapters
                      where the user failed a subchapter (fallback triggered chapter assessment).
                      Hidden entirely in Sub-chapter-wise mode (no final chapter assessment). */}
                  {topicAssessment && !isSubchapterWise() && (getEffectiveTrainingMode() !== 'adaptive' || adaptiveFailedSession) && (() => {
                    const assessmentPassed = assessmentAttempts.some(
                      attempt => attempt.passed || attempt.score_percentage >= (topicAssessment?.pass_score_percentage || 80)
                    );
                    const isAssessmentActive = showAssessment || showPreAssessmentNotes;
                    const filteredSectionsForAssessment = getFilteredSections();
                    const allSubchaptersCompleted = filteredSectionsForAssessment.length > 0 &&
                      filteredSectionsForAssessment.every(s => isSectionViewed(s));
                    const isAssessmentLocked = !allSubchaptersCompleted && !assessmentPassed;
                    return (
                      <button
                        onClick={async () => {
                          if (isAssessmentLocked) {
                            toast.warning('Please complete all subchapters before taking the assessment.');
                            return;
                          }
                          setShowMobileSubchapters(false);
                          if (assessmentPassed) {
                            // Already passed — open assessment directly for review
                            handleTakeAssessmentClick();
                            return;
                          }
                          // Clear adaptive/completion views so the pre-assessment notes screen renders
                          setAdaptiveMode(false);
                          setAdaptiveSubmitted(false);
                          setShowCompletionScreen(false);
                          await fetchPreAssessmentNotes();
                          setShowPreAssessmentNotes(true);
                        }}
                        className={`w-full text-left rounded-lg p-3 border-2 transition-all mt-1 ${
                          isAssessmentLocked
                            ? 'border-gray-200 bg-gray-100 opacity-55 cursor-not-allowed'
                            : isAssessmentActive
                            ? 'border-blue-500 bg-blue-50'
                            : assessmentPassed
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              isAssessmentLocked ? 'bg-gray-400 text-white' : assessmentPassed ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                            }`}>
                              <ClipboardCheck className="w-3.5 h-3.5" />
                            </span>
                            {isAssessmentLocked && <Lock className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                            {!isAssessmentLocked && assessmentPassed && <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className={`font-medium text-sm truncate flex-1 ${isAssessmentLocked ? 'text-gray-400' : 'text-gray-900'}`}>Assessment</div>
                                {isAssessmentActive && !isAssessmentLocked && (
                                  <Badge className="bg-blue-600 text-white text-xs flex-shrink-0">Active</Badge>
                                )}
                              </div>
                              <div className={`text-xs ${isAssessmentLocked ? 'text-gray-400' : 'text-gray-500'}`}>Chapter Assessment</div>
                            </div>
                          </div>
                          {isAssessmentLocked
                            ? <Lock className="w-4 h-4 text-gray-300 flex-shrink-0" />
                            : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          }
                        </div>
                      </button>
                    );
                  })()}

                  {getFilteredSections().length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No subchapters available for this chapter</p>
                      <p className="text-xs mt-1">This chapter may only have assessment questions</p>
                    </div>
                  )}
                  </div>
                  </div>
                </div>
              </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Feedback Modal - Opens after 100% completion */}
    {/* Feedback Modal is now handled by parent (StaffDashboardPage) */}
    {/* Internal feedback modal removed to avoid dual-modal conflict */}
    
    {/* Assessment Submission Loader */}
    {isSubmittingAssessment && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
        <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center space-y-4">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <div className="text-center">
            <p className="text-lg font-semibold text-gray-900">Submitting Assessment</p>
            <p className="text-sm text-gray-600 mt-1">Please wait while we process your submission...</p>
          </div>
        </div>
      </div>
    )}
    <CelebrationOverlay />
    </>
  );
};

export default TrainingContentModal;
